/* English UI catalogue.
 *
 * Structure mirrors the surfaces, not the component tree: `nav`, `dashboard`,
 * `cases`, `quotes`, `finance`, `directories`, `settings`, `profile`, and a
 * shared `common`. A string used by two surfaces lives in `common`; a string
 * used once lives with its surface. This keeps the two catalogues diffable side
 * by side, which is how a missing Russian key gets noticed.
 *
 * Domain vocabularies (`caseStatus`, `quoteStatus`, `docStatus`, `legStatus`,
 * `participantRole`, `modes`, `roles`, `notificationKind`) are top-level rather
 * than nested under a surface, because each is keyed by a union from
 * types/domain and is read from several surfaces at once. Keeping them beside
 * the union they mirror is what makes a new member of that union a visible gap
 * here rather than a silent English leak.
 *
 * Copy rules this file obeys (DESIGN.md, EXPERIENCE.md § Voice and Tone):
 *   · Buttons are 1–3 words, verb-first, sentence case. Never Title Case.
 *   · Errors state what happened, then what to do. Never apologise.
 *   · Empty states say what the surface is FOR and offer the action that fills
 *     it. Where nothing is wrong, say so plainly. Never encourage.
 *   · Blocking messages name the block and the reason together, so the rule is
 *     learned once rather than rediscovered.
 */

export const en = {
  common: {
    appName: 'ASA Gateway',
    /** The em-dash placeholder for an absent value. Shared with lib/format. */
    empty: '—',
    actions: {
      retry: 'Try again',
      retryNow: 'Retry now',
      dismiss: 'Dismiss',
      undo: 'Undo',
      cancel: 'Cancel',
      save: 'Save',
      edit: 'Edit',
      add: 'Add',
      open: 'Open',
      close: 'Close',
      delete: 'Delete',
      upload: 'Upload',
      back: 'Back to the dashboard',
      /** Names an actions column whose header is visually empty. */
      label: 'Actions',
    },
    /* The table/board switch on list pages. "Board" not "kanban": the word
       must read in both catalogues without explanation. */
    view: {
      label: 'View',
      table: 'Table',
      board: 'Board',
    },
    /* The dense table's own two controls (UX-DR-4). Both are per-user,
       per-surface workstation habits kept beside the table/board choice, so the
       copy talks about THIS SCREEN rather than about a profile setting. */
    columns: {
      label: 'Columns',
      description: 'Which columns this screen shows',
      reset: 'Show every column',
      hiddenCount_one: '{{count}} column hidden',
      hiddenCount_other: '{{count}} columns hidden',
    },
    filters: {
      label: 'Filter',
      status: 'Status',
      all: 'All',
      clear: 'Clear filter',
      /* Plural, and a separate key from `clear`: a finance view carries a date
         range AND a facet AND a search, and a button offering to clear "the
         filter" when three are set leaves the reader guessing which one it
         means. */
      clearAll: 'Clear filters',
      activeCount_one: '{{count}} filter',
      activeCount_other: '{{count}} filters',
      emptyTitle: 'Nothing matches this filter',
      emptyDescription: 'Clear the filter to see the whole list again.',
    },
    /* The dense table's two empty states (UX-DR-17). NOTHING YET is this pair;
       NOTHING MATCHES is `filters.emptyTitle` above, reused rather than
       duplicated so the two readings of an empty table stay one decision.
       Deliberately unspecific, because it is the fallback: a surface that can
       say what fills it ("Raise one to bill the client") passes its own. */
    table: {
      emptyTitle: 'Nothing here yet',
      emptyDescription: 'Rows appear here as they are created.',
    },
    /* The paged table's footer. "Rows per page" is the one place in the product
       that says ROWS rather than naming the record: it is a fact about the
       table, not about the quotes in it — which is why the count beside it
       ("6 quotes") comes from the surface instead. */
    pagination: {
      label: 'Pages',
      rowsPerPage: 'Rows per page:',
      previous: 'Previous page',
      next: 'Next page',
      page: 'Page {{page}}',
      position: '{{page}} / {{total}}',
    },
    language: {
      label: 'Interface language',
      en: 'EN',
      ru: 'RU',
      english: 'English',
      russian: 'Русский',
    },
    search: {
      label: 'Search',
      placeholder: 'Search cases, quotes, clients',
      loading: 'Searching…',
      emptyTitle: 'No matches',
      emptyDescription: 'Check the number, or try part of the client name.',
      resultCount_one: '{{count}} match',
      resultCount_other: '{{count}} matches',
      hintNavigate: 'navigate',
      hintOpen: 'open',
      hintClose: 'close',
      kind: {
        case: 'Case',
        quote: 'Quote',
        client: 'Client',
      },
    },
    notifications: {
      label: 'Notifications',
      labelWithCount_one: 'Notifications, {{count}} unread',
      labelWithCount_other: 'Notifications, {{count}} unread',
      emptyTitle: 'Nothing new',
      emptyDescription:
        'Chat replies, status changes, approvals and payments on your quotes and cases appear here.',
  loading: 'Loading notifications',
      markAllRead: 'Mark all read',
      unreadItem: 'Unread — {{kind}}',
    },
    /** Column and field labels shared by more than one table. */
    fields: {
      name: 'Name',
      country: 'Country',
      contact: 'Contact',
      email: 'Email',
      phone: 'Phone',
      owner: 'Owner',
      type: 'Type',
      status: 'Status',
      amount: 'Amount',
      number: 'Number',
      total: 'Total',
      date: 'Date',
    },
    /* The add/edit sheet. Shared by every surface that creates or changes a
     * record, so the commit word is the same everywhere — a user who learned
     * "Save" on one sheet must not meet "Submit" on the next. */
    form: {
      saving: 'Saving…',
      required: 'Required',
      /* Field-level codes the server sends as stable tokens (AD-27). */
      invalid: 'This value cannot be accepted',
      tooLong: 'This is too long',
      malformed: 'This could not be read',
      invalidNumber: 'Enter a number',
      /* A figure that IS a number but carries more precision than the column
         that stores it. Said separately from invalidNumber because "Enter a
         number" over 3.607 sends the reader looking for a fault that is not
         there. */
      tooManyDecimals: 'Use at most 2 decimal places',
      /* Blocking messages name the block and the reason together. */
      fixFields: 'Some fields need attention before this can be saved.',
      active: 'Active',
      inactive: 'Inactive',
      optional: 'Optional',
      /* The summary on every disclosure that hides the rest of a form. One
         word, used on all of them, because a reader who learned where the rare
         fields live on one form must find them in the same place on the next.
         The count beside it is the component's. */
      more: 'More',
      selectPlaceholder: 'Select…',
      searchPlaceholder: 'Search…',
      noMatches: 'No matches',
      noMatchesHint: 'Try a shorter search',
      /* The country picker (ISO 3166-1). The closed control is a button now, so
         its placeholder names the CHOICE; the field that advertises what may be
         typed is the search at the top of the list, below. */
      countryPlaceholder: 'Select country…',
      /* The filter inside the country popup. It names the code forms because
         the search matches them as well as the name, and a box labelled only
         "Search…" does not advertise that "RUS" or "398" work. */
      countrySearch: 'Country, code…',
      /* The phone field's two halves: the calling-code button and the number
         beside it. The button's own text is "+7", which says nothing about what
         it is, so the label supplies the noun. */
      phoneCode: 'Country calling code',
      phoneSearch: 'Country, +code…',
      phonePlaceholder: 'Phone number',
      /* Shown against a saved value that is not one of the 249 codes — a port
         or city entered before this field became a picker. It stays selectable:
         the record is not wrong, it is older than the control. */
      countryCarriedOver: 'as entered',
      /* Announced to the filter's own field as it types, so the effect of a
         keystroke is heard without leaving the box to count rows. */
      matchCount_one: '{{count}} of {{total}} shown',
      matchCount_other: '{{count}} of {{total}} shown',
      clear: 'Clear',
      /* The date picker's own strings. The placeholder shows the ISO shape the
         field accepts rather than a localised example, because the value is
         typed as digits and `YYYY-MM-DD` is the only form it stores. */
      datePlaceholder: 'YYYY-MM-DD',
      openCalendar: 'Open calendar',
      previousMonth: 'Previous month',
      nextMonth: 'Next month',
      today: 'Today',
    },
    /* The unsaved-changes guard on an editing sheet.
     *
     * A question rather than a warning, and it names what is at stake: "are you
     * sure?" over an unnamed loss makes the reader guess what they are agreeing
     * to. The primary action here is CANCELLING the close — the safe one — so
     * a reflexive Enter keeps the draft rather than discarding it. */
    unsaved: {
      title: 'Discard your changes?',
      body: 'This form has edits that have not been saved. Closing it loses them.',
      keep: 'Keep editing',
      discard: 'Discard changes',
    },
    menu: {
      open: 'Open navigation',
      close: 'Close navigation',
    },
    loading: 'Loading',
    /* ONE vocabulary, shared by every finance view and the dashboard
       (components/data/DateRangeControl.tsx). `1W` was removed with the second
       vocabulary that declared it: a week of receivables is noise around a
       single invoice, and two sets of periods let two surfaces answer
       different questions while looking alike. */
    dateRange: {
      label: '{{label}} date range',
      shared: 'Date range',
      '1M': '1M',
      '3M': '3M',
      '1Y': '1Y',
      all: 'All',
    },
    /* Suppressed fields (AD-6). A field a role may not see is ABSENT from its
     * DTO, never nulled, and the UI says so rather than rendering an empty
     * cell — suppression must never be misread as missing data. */
    suppressed: {
      agent: 'Agent hidden',
      carrier: 'Carrier hidden',
      vendor: 'Vendor hidden',
      contact: 'Contact restricted',
      generic: 'Hidden',
      reason: 'Your role does not show this field',
    },
    /** Terse, unitful ages for event feeds — see lib/format.formatRelativeTime. */
    relativeTime: {
      now: 'now',
      minutes: '{{count}} min',
      hours: '{{count}} h',
      days: '{{count}} d',
    },
  },

  nav: {
    label: 'Main',
    sections: {
      main: 'Main',
      company: 'Company',
    },
    dashboard: 'Dashboard',
    quotes: 'Quotes',
    cases: 'Cases',
    chat: 'Chat',
    finance: 'Finance',
    clients: 'Clients',
    directories: 'Directories',
    settings: 'Settings',
    profile: 'Profile',
  },

  roles: {
    sales: 'Sales',
    pricing: 'Pricing',
    operations: 'Operations',
    finance: 'Finance',
    'super-admin': 'Super-admin',
  },

  modes: {
    sea: 'Sea',
    rail: 'Rail',
    road: 'Road',
    air: 'Air',
    multimodal: 'Multimodal',
  },

  caseStatus: {
    draft: 'Draft',
    'in-progress': 'In progress',
    stuck: 'Stuck',
    delivered: 'Delivered',
    // The two-stage close FR-25 runs on, and BOTH halves say which one they
    // are. "Closed" alone was the second lane's label and it was the wrong
    // word beside "Ops closed": read down a board, the pair asked the reader
    // to infer that the unqualified one meant the money, which is exactly the
    // guess a lifecycle label exists to remove.
    'operationally-closed': 'Ops closed',
    'financially-closed': 'Financially closed',
    cancelled: 'Cancelled',
  },

  quoteStatus: {
    draft: 'Draft',
    sent: 'Sent',
    rejected: 'Rejected',
    expired: 'Expired',
    converted: 'Converted',
  },

  docStatus: {
    draft: 'Draft',
    issued: 'Issued',
    'part-paid': 'Part paid',
    paid: 'Paid',
    /* There is no `void` here, deliberately. AD-24 makes Draft → Issued
       one-way and nothing in this application moves a document back out of
       Issued: a correction is a NEW document, which is how the paper world
       does it. A pill for a state no act can produce is an affordance for
       something that cannot happen — the same defect the money section had
       when it told a founder with every role that money was hidden from him.
       `part-paid` and `paid` are not stored either; they are derived on the
       client from the served figures. But they are real readings of a real
       document, which is why those two stay and `void` went. */
  },

  documentKind: {
    invoice: 'Invoice',
    'debit-note': 'Debit note',
    offer: 'Offer',
  },

  legStatus: {
    planned: 'Planned',
    'in-transit': 'In transit',
    'waiting-border': 'Waiting at border',
    arrived: 'Arrived',
    completed: 'Completed',
  },

  participantRole: {
    shipper: 'Shipper',
    consignee: 'Consignee',
    agent: 'Agent',
    'counter-agent': 'Counter-agent',
    'vehicle-owner': 'Vehicle owner',
    dispatcher: 'Dispatcher',
    driver: 'Driver',
  },

  notificationKind: {
    message: 'Message',
    status: 'Status',
    approval: 'Approval',
    payment: 'Payment',
    document: 'Document',
    file: 'File',
    assignment: 'Assignment',
    stuck: 'Stuck',
  },

  recordKind: {
    case: 'Case',
    quote: 'Quote',
    invoice: 'Invoice',
  },

  dashboard: {
    /* The greeting is time-of-day aware. Russian splits the day differently, so
     * the boundaries live in the component and both catalogues simply supply
     * three greetings. */
    greeting: {
      morning: 'Good morning, {{name}}',
      afternoon: 'Good afternoon, {{name}}',
      evening: 'Good evening, {{name}}',
    },
    eyebrow: '{{date}} · Data as of {{time}}',
    subtitle_one: '{{count}} item needs a decision today across shipments and money.',
    subtitle_other:
      '{{count}} items need a decision today across shipments and money.',
    loading: 'Loading dashboard',

    counters: {
      activeCases: 'Active cases',
      shipmentsThisMonth: 'Shipments this month',
      toPay: 'To pay',
    },

    /* The money tiles. A label here is not a promise that the figure exists:
       turnover is still declared `pending` by the server, and its tile states
       its own absence rather than rendering an empty card. */
    tiles: {
      turnover: 'Turnover',
      /* "Profit", not "Forecast profit": FR-51 AC 2 makes this one figure over
         two bases — what a financially closed case actually earned, and what
         every other case is forecast to earn — so naming it after one of the two
         would misdescribe the other half of its own total. */
      profit: 'Profit',
      /* "To collect" and "To pay", not "Accounts receivable/payable": the
         founder's own words, and each says what to do about it. */
      toCollect: 'To collect',
      toPay: 'To pay',
      /* The drill-through affordance (UX-DR-10). One label for every tile
         rather than one per destination: the tile's own heading already says
         which figure is being opened, and "View receivables" beside a heading
         reading "To collect" says it twice. */
      drillThrough: 'View list',
    },

    pending: {
      /* Names the story so the tile reads as sequencing rather than breakage —
         "not yet" instead of "broken". */
      description: 'Available once story {{story}} lands.',
    },

    attention: {
      title: 'Needs a decision',
      emptyTitle: 'Nothing needs a decision',
      /* The threshold comes from the server, so the sentence states the rule
         actually applied rather than a number copied into the client. */
      emptyDescription_one:
        'A shipment appears here after {{count}} day without movement.',
      emptyDescription_other:
        'A shipment appears here after {{count}} days without movement.',
      stuckFor_one: 'No movement for {{count}} day',
      stuckFor_other: 'No movement for {{count}} days',
      /* Which figure is negative on a minus shipment (FR-52). The distinction
         matters to what the founder does next: a forecast is still recoverable
         while the shipment runs, a final figure is the loss as booked. */
      minusBasis: {
        forecast: 'Forecast to lose money',
        final: 'Closed at a loss',
      },
      kind: {
        stuck: 'Stuck',
        minus: 'Minus',
        approval: 'Approval',
        overdue: 'Overdue',
        checklist: 'Incomplete',
        'quote-followup': 'Follow up',
      },
    },

    activity: {
      title: 'Recent activity',
      empty: 'No activity yet',
    },

    aging: {
      caption: '{{clients}} clients · {{tranches}} tranches due this week',
    },
  },

  cases: {
    title: 'Cases',
    subtitle: 'Every shipment in motion, and what it is worth.',
    loading: 'Loading case',
    backToList: 'Back to cases',
    detailNotReady: 'The case detail screen is not connected yet — {{number}} was created and is in the list.',
    sectionsLabel: 'Case sections',
    actions: {
      fromQuote: 'From quote',
      newCase: 'New case',
      editCase: 'Edit case',
      create: 'Create case',
      sendStatus: 'Send status to client',
    },

    /* Telling the client where their shipment is (FR-41).
       The sheet names the recipient before the send, because an email leaves
       permanently and the address is the one fact worth checking first. */
    statusSend: {
      title: 'Send status to client',
      submit: 'Send',
      recipient: 'To',
      status: 'Status',
      language: 'Language',
      languageHint: "The language your client reads, not yours.",
      note: 'Note',
      noteHint: 'Optional. Added below the status and the arrival date.',
      notePlaceholder: 'Anything the client should know beyond the status.',
      /* Not an error: nothing failed, the directory is simply incomplete. */
      noContact:
        'This client has no email contact yet. Add one in the client directory to send them updates.',
      sent: 'Status sent to {{email}}.',
      failed: 'The status was not sent. Check the connection and try again.',
    },

    /* The same act from the LIST: one client, several of their cases, one
       send. Asked in the order the work happens — who first, then which. */
    clientStatusSend: {
      title: 'Send status to client',
      client: 'Client',
      clientPlaceholder: 'Choose a client',
      pickClientFirst: 'Choose a client to see their cases.',
      cases: 'Cases',
      selectAll: 'Select all',
      selectedCount: '{{selected}} of {{total}}',
      submitCount_one: 'Send 1 case',
      submitCount_other: 'Send {{count}} cases',
      noteHint: 'Optional. Added to every case in this send.',
      noContact:
        'This client has no email contact yet. Add one in the client directory to send them updates.',
      /* Distinct from noContact because the action differs: nothing here is
         anybody's mistake, so there is nothing to go and fix. */
      nothingReportable:
        'Every shipment for this client is cancelled or closed, so there is no status to report.',
      notReportable: 'Cancelled or closed — not included',
      sentAll_one: 'Status sent for 1 case.',
      sentAll_other: 'Status sent for {{count}} cases.',
      sentPartial: 'Status sent for {{sent}} of {{total}} cases.',
    },

    /* Creating a case WITHOUT a quote is a primary path (FR-70): repeat
       business runs through it, and the migrated cases arrive quote-less. */
    form: {
      quoteNumber: 'Quote',
      quoteHint: 'The accepted quote this case is converted from.',
      etd: 'ETD',
      /* The FR-32 trio. A pick from four rather than free text: the terms have
         to become dated expectations the platform can chase (FR-33), and no
         parse of a typed phrase yields two dated amounts. */
      paymentTermsKind: 'Payment terms',
      paymentMethod: 'Payment method',
      creditDays: 'Credit days',
      creditDaysHint: 'Days from registration until the whole amount is due.',
      creditDaysInvalid: 'Enter a whole number of days, from 1 to 365.',
      nextStep: 'Legs, participants and money are added on the case itself.',
    },
    /* The four kinds, keyed by the WIRE value so a lookup is
       t(`cases.terms.${kind}`) rather than a switch that silently falls
       through on a kind this build has not heard of. */
    terms: {
      fifty_fifty: '50-50',
      full_on_delivery: 'Full upon delivery',
      credit: 'Credit',
      other: 'Other',
    },
    method: {
      cash: 'Cash',
      bank_transfer: 'Bank transfer',
    },
    columns: {
      number: 'Case',
      client: 'Client',
      route: 'Route',
      leg: 'Leg',
      status: 'Status',
      eta: 'ETA',
      rate: 'Rate USD',
      forecast: 'Forecast USD',
    },
    card: {
      /* The board card's figures block. Short labels, because they are read
         once and then ignored — the column of values beside them is what the
         eye actually runs down. The dates keep their shipping-trade names:
         ETD and ETA are what the desk says out loud, in both languages. */
      rate: 'Rate',
      forecast: 'Forecast',
      /* Where the cargo is on the route, on the board card. A POSITION, not a
         countdown — "Leg 2/3" says both how far it has come and how much is
         left, where "1 leg to go" says only the second and reads as a promise. */
      leg: 'Leg {{sequence}}/{{total}}',
      etd: 'ETD',
      eta: 'ETA',
      /** The compact stalled marker on a card, where the full sentence will not fit. */
      stalledShort: '{{count}} d',
    },

    /* Dragging a card to another lane is a real status change (FR-25, AD-24).
       The message names the case because the card has already slid back by the
       time it is read, and points at the one refusal a person can act on. */
    board: {
      moveRefused:
        'Case {{number}} was not moved. The server refused the change — a financial close needs every balance settled first.',
      /* An empty lane, in words. The invitation is a SECOND key because it is
         only true of a lane something may actually be dragged into — printing
         it over Cancelled would teach a gesture that lane refuses. */
      laneEmpty: 'No cases',
      laneDrop: 'Drag one here to change its status.',
    },

    /* The lifecycle on the RECORD (FR-25, FR-37, AD-24), where the board's
       drag has an opposite number that stops to ask. Two of the moves have
       consequences a person should not discover having caused, so the copy
       names what happens rather than asking "are you sure". */
    lifecycle: {
      /* FR-25's refusal, naming WHICH balance is outstanding rather than
         grateful silence over a greyed-out control. */
      unsettledBalances:
        'This case cannot be closed financially while money is outstanding: {{items}}.',
      label: 'Case lifecycle',
      /* The status names come from the `caseStatus` catalogue above, so a
         relabelled state changes here too rather than in two places. */
      change: 'Change status',
      moveTo: 'Move to {{status}}',
      moving: 'Moving…',

      confirmTitle: 'Confirm the change',
      confirmFinancialBody:
        'Closing {{number}} financially ends the money side of this case: it stops accepting invoices and payments. Only a Super-admin can reopen it afterwards, and the reopening is recorded.',
      confirmFinancialAction: 'Close financially',
      confirmCancelBody:
        'Cancelling {{number}} is permanent. The case keeps its history and its quote link, but it cannot be moved to another status afterwards.',
      confirmCancelAction: 'Cancel case',

      /* Not a generic refusal: the server names the balances that are holding
         the close, and they are listed under this line (FR-25). */
      balancesTitle: 'This case cannot be closed financially yet',

      reopen: 'Reopen',
      reopenTitle: 'Reopen {{number}}',
      reopenSubmit: 'Reopen case',
      reopenBody:
        'The case returns to Ops closed. Reopening undoes the financial close only — the delivery is not reopened with it.',
      reason: 'Reason',
      reasonHint:
        'Recorded in the case history with your name. Reopening without a reason is not possible.',
    },
    stalled_one: 'No movement for {{count}} day',
    stalled_other: 'No movement for {{count}} days',
    emptyTitle: 'No cases yet',
    emptyDescription:
      'Cases you convert from a quote, or create directly, appear here with their rate and forecast.',

    header: {
      rate: 'Rate',
      cost: 'Cost',
      forecast: 'Forecast',
      mode: 'Mode',
    },

    /* "Team" is the COLLEAGUES working the case; the parties to the shipment
       are counterparties. One word used to stand for both — the Participants
       tab listed the shipper and the consignee while the people who actually
       run the case were a card on Overview. Two different questions, two
       sections, two words. */
    tabs: {
      overview: 'Overview',
      legs: 'Legs',
      team: 'Team',
      participants: 'Counterparties',
      money: 'Money',
      documents: 'Invoices',
      files: 'Files',
      history: 'History',
    },

    overview: {
      route: 'Route',
      /* "rail + road via Khorgos" — modes come in already localised. */
      routeVia: '{{modes}} via {{border}}',
      stopRole: {
        loading: 'POL',
        border: 'Border crossing',
        discharge: 'POD',
        unloading: 'Unloading place',
      },
      departed: 'departed {{date}}',
      arrivedOn: 'arrived {{date}}',
      hereNow: 'here now',
      hereNowFor: 'here now, {{days}} d',
      etaOn: 'ETA {{date}}',

      cargo: 'Cargo',
      imo: 'IMO {{class}}',
      kg: 'kg',
      cbm: 'CBM',
      declaredTotal: 'Declared total',
      measuredAt: 'Measured at {{location}}',

      containers: 'Containers and seals',
      containersEmptyTitle: 'No containers yet',
      containersEmptyDescription:
        'Container and seal pairs populate the documents this case issues.',
      seal: 'Seal',
      equipment: 'Equipment',

      details: 'Case details',
      dates: 'Dates',
      incoterm: 'Incoterms',
      paymentTerms: 'Payment terms',
      paymentMethod: 'Payment method',
      creditDays_one: '{{count}} day',
      creditDays_other: '{{count}} days',
      responsible: 'Responsible',
      opsNamed: 'Ops: {{name}}',
      entity: 'Issuing entity',
      packingList: 'Packing list',
      packingListReceived: "Final list received — client's figures",
      packingListEstimate: 'Not received — our estimate',
      notes: 'Notes',
    },

    legs: {
      /* FR-67's refusal. The names come from problem.errors.expenses and are
         interpolated, never concatenated in English on the server — see
         refusalMessage in CaseSections.tsx. */
      carriesExpenses:
        'This leg cannot be deleted while costs are recorded against it: {{items}}. Move or remove them first.',
      add: 'Add leg',
      edit: 'Edit leg',
      /* The card's accessible name: it is a LINK to the leg's page now rather
         than a block that opens a form. It names the route as well as the
         number, because "Open leg" four times in a list of four cards does not
         say which one is being opened. */
      open: 'Open leg {{n}}: {{from}} → {{to}}',
      carrier: 'Carrier',
      /* The carrier picker (FR-40). Two of the seven types are offered — the
         counterparty who moves cargo and the company whose truck it is — and
         the row says which, because a haulier and a truck owner are different
         relationships and the same company is sometimes both. */
      carrierPlaceholder: 'Select carrier…',
      carrierSearch: 'Carrier name…',
      /* Shown against a saved carrier that matches no directory row: a name off
         a waybill, entered before this field became a picker. It stays
         selected — the record is not wrong, it is older than the control. */
      carrierAsEntered: 'as entered',
      /* Shown against a linked carrier the desk has since deactivated. Still
         selectable, for the reason above: the movement happened. */
      carrierInactive: 'inactive',
      /* The escape hatch inside the picker: the carrier standing in front of
         somebody is frequently not in the directory yet, and making them leave
         the leg to add one is how a leg gets saved with no carrier at all. */
      carrierAdd: 'Add a counterparty',
      reference: 'Reference',
      /* The country beside each endpoint (FR-19). The PLACE stays what it was —
         a terminal, a siding, a yard — and this is the second, structured fact
         a corridor report groups by. */
      originCountry: 'Origin country',
      destinationCountry: 'Destination country',
      /* The section's summary line: "2 legs · rail + road via Khorgos". */
      count_one: '{{count}} leg',
      count_other: '{{count}} legs',
      legN: 'Leg {{n}}',
      /* The leg's paper, in the trade's words: CMR on the road, the waybill
         everywhere else. */
      waybill: 'Waybill',
      cmr: 'CMR',
      notIssued: 'not issued',
      /* The CONVEYANCE, labelled per mode (FR-26). One label reading "flight /
         voyage number" would make the desk work out which half applies to them
         on every leg; the card and the sheet know the mode already. */
      flightNumber: 'Flight',
      voyageNumber: 'Voyage',
      vehicles: 'vehicles',
      trucks: 'Trucks',
      /* The plate repeater (FR-67). Plural on ONE leg: a consignment crossing
         on two trucks is one leg and two vehicles. */
      addPlate: 'Add truck',
      removePlate: 'Remove truck',
      plateN: 'Plate {{n}}',
      platePlaceholder: 'TM-AH 4471',
      /* Containers as CNTR/SEAL pairs (FR-67). */
      containers: 'Containers',
      addContainer: 'Add container',
      removeContainer: 'Remove container',
      containerN: 'Container {{n}}',
      containerPlaceholder: 'MSKU 0419871',
      sealN: 'Seal {{n}}',
      sealPlaceholder: 'SL-88421',
      containerTypeN: 'Type {{n}}',
      containerTypePlaceholder: '40HC',
      /* An unsealed box says so. Empty means NOT YET SEALED or not yet known —
         never "unsealed" — and a blank beside the number would read as a
         container nobody had to seal. */
      sealNotRecorded: 'seal not recorded',
      datePlanned: '{{date}} planned',
      /* The slip against the ETA, beside the actual: "20 Jul (+1 d)". */
      delay: '(+{{count}} d)',
      /* How long the leg is planned to take, SERVED (casefile.Leg.TransitDays)
         rather than subtracted here — two rules about which end counts is two
         answers on one screen. */
      duration: 'Duration',
      days_one: '{{count}} day',
      days_other: '{{count}} days',
      /* Zero days is a real span, not a missing one: a truck that loads and
         delivers between two towns does it in a morning. Said in words, because
         "0 days" reads as a leg nobody has dated. */
      sameDay: 'same day',
      /* The route strip's span: the first departure to the last arrival. No day
         count beside it — the legs carry the counts the server computes, and a
         total added up here would be a second rule about gaps between legs. */
      span: '{{from}} — {{to}}',
      spanFrom: 'from {{date}}',
      /* The marker on the leg the cargo is on. The server decides which one
         (casefile's `legnow`); this only names it. */
      currentLeg: 'Current leg',
      expenses: 'Expenses',
      plannedExpense: 'Planned expense',
      /* What has gone out to whoever moves this leg, and what is left on it
         (issue #35 story 6). Composed per leg from finance's own read, so the
         card and the direction's page state one figure. "Carrier" in both
         labels because the case's own payables are a different question. */
      paidToCarrier: 'Paid to carrier',
      carrierOutstanding: 'Owed to carrier',
      /* The bulk truck table on the direction's page (FR-67, story 6). A
         direction is routinely booked as six trucks in one call, so the strip
         on top fills the new rows and each row may still differ. */
      vehiclesTable: {
        planArticle: 'Plan line',
        mixedPlans: 'Different plan lines',
        threeRows: 'Add three rows',
        newTruck: 'New truck',
        /* The pencil on a row. Named by the truck, not by the number: three
           rows carry the same icon and a screen reader hearing "Edit, Edit"
           cannot tell which truck it is about to open. */
        editRowNamed: 'Edit truck {{plate}}',
        groupedColumns: {
          transport: 'Transport', people: 'Carrier and driver', weight: 'Weight, kg',
          rate: 'Rate', paid: 'Paid', actions: 'Actions',
        },
        title: 'Trucks and rates',
        subtitle:
          'The shared fields fill new rows; each truck can carry its own carrier and its own rate.',
        /* The strip of values NEW rows are born with. Adding a row is the
           bar under the table; this only reveals those fields. */
        sharedFields: 'Shared fields',
        oneMoreRow: 'One more row',
        saveTrucks: 'Save trucks',
        defaultCarrier: 'Default carrier',
        defaultRate: 'Default rate',
        currency: 'Currency',
        defaultsNote: 'filled into new rows',
        /* Which generalized quote line these trucks' rates detail (spec
           2026-09-01-expense-detail-grouping, L7). Without it the case plans
           the same freight twice — once off the quote, once per truck — so the
           control is named for the relationship rather than for the field. */
        detailOfLabel: 'Details the plan line',
        detailOfNone: 'not detailing',
        detailOfFailed:
          'The trucks and rates are saved, but re-filing the plan lines under the chosen group failed.',
        /* Opening a saved row for editing. The action, not the number: the
           button SHOWS the row number and says what pressing it does. */
        editRow: 'Edit row {{n}}',
        columns: {
          plate: 'Truck',
          trailer: 'Trailer',
          container: 'Container',
          cmr: 'CMR no.',
          carrier: 'Carrier',
          driver: 'Driver',
          phone: 'Phone / WhatsApp',
          weight: 'Weight, kg',
          rate: 'Rate',
          advance: 'Advance',
          paid: 'Paid',
          pay: 'Payment',
        },
        /* The act on a saved row. Named for the truck rather than the visible
           word alone: several rows carry the same "Pay", and a screen reader
           hearing "Pay, Pay" cannot tell which truck it is about to pay for. */
        payRow: 'Pay',
        payRowNamed: 'Pay for {{plate}}',
        removeRowNamed: 'Delete truck {{plate}}',
        /* What a new truck's rate is recorded AS: one planned cost line on the
           case's money, named for the truck it was agreed for. */
        rateItem: 'Truck rate {{plate}}',
        newTrucksNote_one:
          '{{count}} new truck · its rate will be recorded as a planned cost on this leg',
        newTrucksNote_other:
          '{{count}} new trucks · their rates will be recorded as planned costs on this leg',
        /* HALF the save landed, and the sentence says which half. Anything
           vaguer would have somebody re-enter six trucks that are already
           saved. */
        savedTrucksRatesFailed_one:
          'The trucks are saved; {{count}} rate was not recorded.',
        savedTrucksRatesFailed_other:
          'The trucks are saved; {{count}} rates were not recorded.',
        retryRates: 'Record the rates',
        /* The two figures a row can get wrong. Said rather than dropped: a rate
           that does not parse used to save the truck with no rate at all, and a
           weight that does not parse used to erase the kilograms already
           stored — both under a save that reported success. */
        rateInvalid_one: '{{count}} rate is not a number — for example 1200 or 1 200,50.',
        rateInvalid_other:
          '{{count}} rates are not numbers — for example 1200 or 1 200,50.',
        weightInvalid_one: '{{count}} weight is not whole kilograms — digits only.',
        weightInvalid_other: '{{count}} weights are not whole kilograms — digits only.',
      },
      /* Why saving the leg is unavailable. The write replaces the WHOLE leg,
         truck rows included, so a caller who may not read the carrier and the
         driver (AD-6) would submit them blank and erase them. One sentence for
         both forms — the page's edit card and the trucks table — because both
         send the same PUT, and two explanations of one refusal part company on
         the first correction. Said here rather than after a press that quietly
         destroyed data. */
      identityHidden:
        'The carrier and driver on this leg are not shown to your role, and saving would clear them. Ask someone with commercial access to make the change.',
      /* `createdTemplateFailed` was here and is gone with the surface that
         needed it: the create no longer holds the reader on a form when only the
         template apply is refused. It lands them on the leg that exists, with
         the refusal above an empty chain and «Apply template» beside it, which
         is the ordinary state of a leg nobody has given a corridor plus one
         sentence — rather than a mode with its own vocabulary. */
      /* Where the leg was last seen (FR-24). The server has carried this since
         Story 3.4 and nothing rendered it. */
      lastLocation: 'Last location',
      reportedAt: 'reported {{at}}',
      /* Deleting a leg. The refusal is the control (FR-67): a leg carrying cost
         lines cannot be deleted, and the server names them. */
      delete: 'Delete this leg',
      deleteConfirm: 'Delete this leg? Its dates, points and containers go with it.',
      deleteConfirmAction: 'Delete leg',
      emptyTitle: 'No legs yet',
      emptyDescription:
        'Add the legs this shipment moves through to track it from origin to delivery.',
    },

    /* One leg on its own page (issue #35 story 3).
     *
     * A leg became a URL because it acquired a ROUTE CHAIN: where the cargo has
     * got to, how many days each transition is allowed, and when the next point
     * is due. A chain is read across, not down a column the width of a modal.
     *
     * The RUSSIAN catalogue calls this «Направление» rather than «Плечо» — the
     * word the desk uses for the named routes this is filled from. English
     * keeps "leg", which is the trade's own English word; the keys are the same
     * on both sides, which is what the parity check is about.
     *
     * The wording keeps two facts apart. The CHAIN is the position; the STATUS
     * is the movement report — a leg can sit at `waiting-border` while its last
     * reached point is Almaty, and one control answering both would be wrong
     * half the time. */
    legPage: {
      allPoints: 'All {{count}} points',
      nextPoint: 'Next point',
      routeEnd: 'End of route',
      positionAutomatic: 'Position calculated automatically',
      positionReported: 'Arrival reported by an operator',
      positionUnknown: 'Position not yet recorded. Open the route points to record an arrival.',
      dateLabels: { etd: 'Planned departure', eta: 'Planned arrival', atd: 'Actual departure', ata: 'Actual arrival' },
      back: 'back to the case',
      /* A neutral tag: the chain carries normative days, so the position
         advances on its own (story 4). A FACT about this leg rather than a
         setting — a hand-typed chain with no day counts does not move, and a
         tag on it would promise a movement that is never coming. */
      autoTag: 'Auto',
      /* The subline: who carries it, on what paper, when it left and when it is
         expected. Absent clauses are dropped rather than dashed out. */
      departs: 'departs {{date}}',
      departed: 'departed {{date}}',
      arrives: 'expected {{date}}',
      arrived: 'arrived {{date}}',

      routeTitle: 'Route',
      routeSubtitle:
        'The route points and the days allowed between them. The position advances along them automatically; press a point to record an actual arrival when it is known sooner.',
      routeEmpty:
        'This leg has no points yet. Apply a template, or set the route by hand.',
      /* Pressing a disc. Two labels, because they are two different acts, and
         both name the point: a disc says nothing to a screen reader on its own,
         and "Record arrival" without the name would not say which. The name is
         quoted and left uninflected — the Russian catalogue cannot decline an
         arbitrary place name after a preposition, and both say the same thing in
         the same shape. */
      markThrough: 'Record arrival through “{{point}}”',
      clearFrom: 'Clear the arrival at “{{point}}” and every point after it',
      /* A refusal rather than a permission: on a departed leg the server's own
         `stampDeparture` re-marks the origin, so clearing it would undo itself.
         A control that visibly does nothing reads as broken tracking, so the
         reason is in the label. */
      departureHeld:
        'This leg has departed: the origin arrival is cleared by changing the status, not here.',
      /* The norm under each transition. No plural forms: the Russian
         abbreviation does not inflect, and the delay chip above takes the same
         shape. */
      normDays: '{{count}} d',
      /* Whose word an arrival is on — two different degrees of certainty about
         where a client's cargo is. One word for both would let an assumption be
         read back as a confirmation. */
      pointReachedFact: 'actual',
      pointReachedAuto: 'auto',
      pointExpected: 'exp. {{date}}',
      currentPoint: 'Current point',
      /* An overdue transition. Quiet, because on a corridor with a normative
         time this is the ORDINARY course of events — the position moves by
         itself — and the strip exists for the one correction only a human can
         make. */
      overdueBanner_one:
        '{{from}} → {{to}} is {{count}} day overdue. The position will advance automatically; record the actual arrival if the cargo is already there.',
      overdueBanner_other:
        '{{from}} → {{to}} is {{count}} days overdue. The position will advance automatically; record the actual arrival if the cargo is already there.',
      /* A colon rather than "Cargo at Sarahs": the Russian side cannot inflect
         an arbitrary place name after its preposition, and the two catalogues
         say the same thing in the same shape. */
      cargoAtNext: 'Record arrival: {{point}}',
      notContiguous:
        'A point cannot be marked reached while the one before it is not: record that one first.',

      /* Typing the chain by hand. It sits beside «Apply template» in the card's
         header: both write the same chain, at two different costs of typing. */
      routeEdit: 'Edit route',
      routeEditTitle: 'Edit the route',
      routeEditSubtitle:
        'The points in travel order, with the norm for each transition. The first and the last set the leg’s own “from” and “to”.',
      /* A notice rather than an error: nothing has gone wrong, this is a
         standing property of saving. The server carries arrivals over BY
         POSITION rather than by name, so moving a row leaves the arrival where
         it was in the list. */
      routeEditArrivalsPinned:
        'Arrivals are pinned to a position in the chain rather than to a point’s name: moving a row leaves the arrival where it was in the list.',
      routeEditTooFew:
        'A route of one point is not a route — it holds no transition. Add a second point, or remove the only one.',
      routeEditTooMany: 'A route holds at most {{max}} points.',
      routeEditSave: 'Save route',
      routeEditEndsHint: 'The leg’s ends follow the first and last point',
      arrivalHeldHere: 'An arrival is recorded at this position',
      movePointUp: 'Move point {{n}} up',
      movePointDown: 'Move point {{n}} down',

      applyTemplate: 'Apply template',
      applyTemplateConfirm: 'Apply',
      applyTemplateWarning:
        'Applying replaces the current route points, including the arrivals recorded against them. This cannot be undone.',
      directionTemplate: 'Direction (template)',
      directionTemplateHint:
        'The points are COPIED onto the leg: editing the catalogue afterwards does not move this shipment.',
      directionTemplateNone: 'No template',

      /* The trucks moved to `cases.legs.vehiclesTable` in story 6, where they
         are editable and carry their rates. This is what the table says when
         the direction has none yet. */
      vehiclesTitle: 'Vehicles',
      vehiclesEmpty: 'No trucks recorded on this leg.',

      editTitle: 'Edit this leg',
      editSubtitle:
        'Saving replaces the whole record: a field cleared here is cleared, not left as it was.',
      /* The actual dates, in the same letters as the planned ones beside them —
         the trade's own words in both offices. */
      atd: 'ATD',
      ata: 'ATA',
      stallNote: 'Reason for the delay',
      lastLocationHint: 'Where the last report about the cargo came from.',
    },

    /* Adding a leg, on its own page at `/cases/:caseId/legs/new`.
     *
     * The create left the sheet for the reason the record itself did: a
     * direction is a page, and a create that filled a modal and then landed the
     * reader on a page was the one surface still saying otherwise. The wording
     * here does the same work the sheet's did — it says where the rest of the
     * questions are asked, so nobody hunts on this form for the chain, the
     * trucks or the money. */
    legNew: {
      title: 'New leg',
      /* Says where the reader is going NEXT, which is the whole reason this
         form is short: everything it does not ask is asked on the page a
         successful save lands on. */
      subtitle:
        'Where it starts, where it ends and how it moves. The route points, the trucks and the money are asked on the leg’s own page, which is where saving takes you.',
      formSubtitle: 'Two ends and a mode are required; the rest can wait.',
      create: 'Create leg',
      /* Why the two ends are shown rather than asked. Applying the corridor
         calls `SetLegEnds` in the same transaction, so anything typed here would
         be overwritten by exactly these values a second later — the form would
         be asking a question whose answer it discards. */
      endsFromTemplate: 'From the chosen direction',
      /* The sections this record is about to acquire. Named rather than absent,
         so the page the reader lands on after Create is recognisably the page
         they were just looking at. */
      routeLater:
        'The corridor’s points land here once the leg exists; after that they can be edited and marked off as the cargo passes them.',
      vehiclesLater: 'Plates, drivers and the rates agreed with each carrier, once the leg exists.',
      moneyLater: 'Agreed, paid and outstanding, once the leg exists.',
    },

    /* One direction's money (issue #35 story 6).
     *
     * The block is served to EVERY caller who reaches the case, with the
     * haulier's identity suppressed per field inside it — so this wording never
     * says "not shown for your role" about a figure. The advance/paid split is
     * the server's answer under the ATA rule, and the copy is careful not to
     * describe it as anything this screen worked out. */
    legFinance: {
      title: 'Payments to the carrier',
      subtitle:
        'The advance and the payments on this leg are counted from the payments recorded against the case.',
      payCarrier: 'Record payment',
      agreed: 'Agreed',
      advance: 'Advance',
      paidTotal: 'Paid in total',
      outstanding: 'Outstanding',
      /* The tile sublines: what each figure is counted over. */
      trucks_one: '{{count}} truck',
      trucks_other: '{{count}} trucks',
      beforeArrival: 'before arrival',
      paymentCount_one: '{{count}} payment',
      paymentCount_other: '{{count}} payments',
      perVehicle_one: 'across {{count}} truck',
      perVehicle_other: 'across {{count}} trucks',
      /* The neutral tag on a settlement that moved before the leg arrived. A
         SERVER fact; the table renders it and never re-derives it. */
      advanceTag: 'advance',
      /* The record has not had its second pair of eyes yet (0059, L3). It says
         nothing about the MONEY: a pending settlement is inside every figure on
         this card exactly like a confirmed one (L4), so the wording is about
         the record — "not confirmed", never "not paid". */
      pendingTag: 'not confirmed',
      columns: {
        date: 'Date',
        item: 'For',
        vehicle: 'Truck',
        method: 'Method',
        amount: 'Amount',
        decisions: 'Actions',
      },
      noSettlements: 'Nothing has been paid on this leg yet.',
      /* The per-truck list, and the act on each row. It replaced «record the
         cost first»: a rate nobody has invoiced is no longer a step to clear
         before paying, because paying recognises the debt in the same request. */

      /* «Оплатить перевозчику» — one sheet for the debt, the payment and what
         it settles. The copy never promises the figures will wait for anybody:
         the payment counts the moment it is recorded, and confirmation is a
         check on the record (L4). */
      pay: {
        title: 'Pay the carrier',
        submit: 'Pay',
        atomicNote:
          'The cost, the payment and what it settles are recorded together. Finance confirms the record afterwards; the money counts straight away.',
        noVehicles: 'This leg has no trucks yet, so there is nothing to pay for.',
        vehicle: 'Truck',
        vehicleHint: 'Which truck the money is for. It decides the cost below.',
        vehiclePlaceholder: 'Choose a truck',
        vehiclePosition: 'truck {{position}}',
        chooseVehicleFirst: 'Choose a truck to see what is owed on it.',

        debtHeading: 'What is owed',
        /* A cost already recorded. Shown, not editable: amending a figure that
           exists is the money table's act, and a second way to change it is a
           second answer to what the carrier invoiced. */
        existingActual: 'Already recorded: {{item}} — {{amount}}',
        chooseLine: 'Which cost',
        chooseLineHint:
          'This truck has more than one recorded cost. Nothing is chosen for you.',
        debtAmount: 'Debt',
        debtFromPlan:
          'The agreed rate is {{amount}}. Change it if the carrier invoiced something else.',
        debtUnplanned:
          'No rate was agreed for this truck, so say what is owed. The cost is recorded as unplanned.',
        debtComment: 'Reason',
        debtCommentHint: 'Optional. It is kept with the cost, not sent to anybody.',
        debtCommentRequired:
          'This is above the agreed {{amount}} — say why. The reason is kept with the cost.',

        paymentHeading: 'The payment',
      },
      unavailableTitle: 'The money on this leg could not be read',
      unavailableDescription:
        'Nothing is lost. Try again — if it keeps failing, there may be no exchange rate on the date of one of the amounts.',
    },

    team: {
      title: 'Team',
      /* The section stands on its own now rather than beside Overview, so it
         says what adding somebody DOES: membership IS the access (AD-5, FR-56),
         not paperwork. Same sentence as the quote's. */
      hint: 'Colleagues who work this case. Anybody added sees it from their next request.',
      assign: 'Assign colleague',
      colleague: 'Colleague',
      assignHint:
        'They see this case from their next request. Deactivated accounts are not listed.',
      owner: 'Owner',
      remove: 'Remove {{name}} from the team',
    },
    /* The parties to the shipment, not the colleagues running it — see the note
       on `tabs`. The KEY stays `participants` because that is what the table,
       the endpoint and the DTO are called, and renaming a contract to match a
       label is how the two stop being traceable to each other. */
    participants: {
      title: 'Counterparties',
      add: 'Add counterparty',
      edit: 'Edit counterparty',
      role: 'Role',
      notifyByEmail: 'Notify by email',
      contactHint: 'Used for the notifications this counterparty receives.',
      /* The tickbox arrives already ticked (FR-48), so the sheet has to be able
         to ask for the address that makes it mean anything. Same sentence as
         the quote sheet's — the same rule stated twice in different words reads
         as two different rules. */
      contactNeededForEmail: 'Add an email address to notify this party.',
      /* The section's headline: the count and the delivery rule in one breath. */
      count_one: '{{count}} counterparty',
      count_other: '{{count}} counterparties',
      externalNote: 'external parties are reached by email only',
      notifiedByEmail: 'Notified by email',
      noContact: 'No contact',
      emptyTitle: 'No counterparties yet',
      emptyDescription:
        'Shippers, consignees, agents and drivers assigned to this case appear here.',
      delete: 'Remove this party',
      deleteConfirm: 'Remove this party from the case?',
      deleteConfirmAction: 'Remove party',
    },

    money: {
      /* `forbiddenTitle` and `forbiddenDescription` are DELETED, not moved. They
         described a state the server cannot produce: AD-5 gives a deal's money to
         the deal's own team, and a caller who cannot reach the case gets a 404 on
         the case itself. What they actually rendered was an unpriced case — the
         state every direct creation and all 241 migrated cases start in. */
      /* No block at all: the read or the FX lookup behind it failed. Names the
         block and the reason together, and never apologises. */
      unavailableTitle: 'The money for this shipment could not be read',
      unavailableDescription:
        'Nothing is lost. Try again — if it keeps failing, the rate for a booked currency may be missing.',
      /* Nothing recorded yet, which is not the same as nothing shown. Says what
         the surface is for and offers the action that fills it (UX-DR-17). */
      untouchedTitle: 'Nothing recorded against this shipment yet',
      untouchedDescription:
        'Costs you record appear here against the plan. The agreed rate is set by Finance on the record.',
      title: 'Money · plan vs actual',
      /* The subtitle states the rule the whole card lives under (FR-36): the
         plan came off the accepted quote, and the rate only moves by approval. */
      subtitleFromQuote:
        'Planned lines carried from quote {{number}} · rate frozen, changes need approval',
      subtitle: 'Rate frozen — changes need approval',
      recordActual: 'Record actual expense',
      addExpense: 'Add expense',
      editExpense: 'Edit expense',
      /* The line this sheet cannot edit. A plan settled by SEVERAL invoices
         (AD-4) has no single actual figure, so the folded form has nothing to
         hold and the server refuses the save with a 409. The copy names the
         line and the count, because "cannot be edited" without either reads as
         a malfunction rather than as a fact about this particular line. */
      severalActualsTitle: 'Settled by several invoices',
      severalActualsDescription:
        '“{{description}}” has {{lines}} recorded costs against one plan, so there is no single actual figure to edit here. Change the invoices where they were recorded.',
      /* Deleting a cost line removes the LINE: the plan and every actual
         against it, in one transaction. It used to remove only the actual and
         leave the plan on screen looking undeleted. */
      deleteExpense: 'Delete this line',
      /* The row's own two icons. Named by the LINE, not by the act alone: a
         table of eight costs carries eight identical pencils, and a screen
         reader hearing "Edit, Edit" cannot tell which one it is opening. */
      editExpenseNamed: 'Edit “{{line}}”',
      deleteExpenseNamed: 'Delete “{{line}}”',
      deleteExpenseConfirm: 'Delete this cost line?',
      deleteExpenseConfirmAction: 'Delete line',
      settled: 'Settled',
      /* Variance is derived, never entered — nothing in the product has a
         "recalculate" button. */
      actualHint: 'Leave empty until the cost lands. Variance is calculated.',
      totals: 'Totals',
      /* Under the line it explains, not in a banner above the card — the row
         itself carries the attention wash while the Super-admin decides. */
      passThroughPending: 'Pass-through to client pending approval',
      expensesEmptyTitle: 'No expense lines yet',
      expensesEmptyDescription:
        'Planned lines carry over from the quote; actuals are recorded here as invoices land.',
      clientReceivable: 'Client receivable',
      /* The receivable's provenance (FR-32, FR-33). `tranchePaid` is gone with
         `ReceivableTranche.settled`: per-tranche settlement needs the
         allocations against that tranche, and the block serves only the case's
         receivable total — a paid/unpaid distinction the client cannot compute
         is one it must not render. */
      trancheExpected: '#{{n}} {{amount}} expected {{date}}',
      trancheUndated: '#{{n}} {{amount}} no date yet',
      /* The editable strip. A tranche with no date is not overdue — the second
         half of a 50-50 case starts undated, waiting for delivery. */
      tranchesTitle: 'Expected receipts',
      trancheN: 'Tranche {{n}}',
      trancheDue: 'Expected',
      /* The picker's own name, and it names the TRANCHE: a 50-50 case has
         two of these one under the other, and a series read out as
         "Expected, Expected" is a series nobody can act on. */
      trancheDueNamed: 'Expected date of tranche {{n}}',
      /* The two columns the series aligns under. Said once over the column
         instead of once per row — see TrancheStrip's own note. */
      trancheColumns: { tranche: 'Tranche', share: 'Share', amount: 'Amount' },
      /* The row's own name. The sequence is the series' MEANING — «the
         second tranche» is what Operations chase — and the count beside it
         is what makes «1» mean a half rather than the whole. */
      trancheOfSeries: 'Tranche {{n}} of {{total}}',
      payables: 'Payables',
      dueOn: 'due {{date}}',
      pendingApprovalLabel: 'Pending approval',
      pendingNote: 'Extra charge to client · awaiting Super-admin',
      noPending: 'Nothing awaiting approval',

      /* FR-30's three figures. Three labels and never one "profit": the
         workbook this replaces used one word for all three, which is how two
         people quoting the same shipment quoted different margins. */
      expectedProfit: 'Expected profit',
      expectedProfitNote: 'Agreed rate less planned costs',
      forecastProfitLabel: 'Forecast profit',
      forecastProfitNote: 'Costs to date plus what is still planned',
      finalProfit: 'Final profit',
      finalProfitNote: 'Money received less money paid, at settlement rates',
      noRateYet: 'No rate agreed yet — this is planned costs alone',

      payments: {
        heading: 'Payments on this case',
        emptyTitle: 'No payments recorded',
        emptyDescription:
          'Money in and out is recorded on the Money page and appears here with what it settles.',
        unallocated: '{{amount}} still unallocated',
        /* The five facts about a payment, one column each. «Fee» is its own
           column and never part of «Amount»: the amount is what SETTLED,
           the fee is what the transfer COST, and no surface may total the
           two (AD-11). */
        columns: {
          payment: 'Payment',
          fee: 'Transfer fee',
          unallocated: 'Unallocated',
          amount: 'Amount',
          date: 'Received',
        },
      },

      columns: {
        description: 'Description',
        counterparty: 'Counterparty',
        planned: 'Planned',
        actual: 'Actual',
        line: 'Expense line',
        vendor: 'Vendor',
        leg: 'Leg',
        plannedIn: 'Planned {{currency}}',
        actualIn: 'Actual {{currency}}',
        variance: 'Variance',
        actions: 'Actions',
      },
      /* What a cost paid for, linked to that leg's page (FR-67, story 6). The
         plate is carried when the row names a truck — "which leg" and "which
         truck" are one question at the desk. */
      legLink: 'Leg {{n}}',
      legLinkVehicle: 'Leg {{n}} · {{plate}}',
      /* A cost that belongs to no single movement — a clearance fee, a bank
         charge. Legitimate and common, and said in words: a blank cell would
         read as a row somebody forgot to file. */
      unattributed: 'unattributed',
      /* A plan detailed per truck (spec 2026-09-01-expense-detail-grouping).
         The parent's own figure has stopped counting in the totals, so it is
         said HERE, as the benchmark it became: what the quote allowed, and how
         far the rates actually agreed have moved from it. Across currencies
         there is no delta to state and the note counts the lines instead. */
      groupNote: 'quote {{quota}} · {{delta}} · {{count}} lines',
      groupNoteMixed: '{{count}} detail lines',
      toggleGroup: 'Toggle details of {{line}}',
    },

    documents: {
      title: 'Invoices and debit notes',
      /* Says what separates this from Files: paper we RAISED, not paper that
         was scanned and attached. */
      subtitle: 'The paper raised against this case, and what is still open on it.',
      raise: 'New document',
      pick: {
        title: 'Charge from case costs',
        description:
          'Tick the recorded costs to bill on. They become editable charges on the document — you can add more by hand, or continue with none and type them yourself.',
        confirm: 'Continue',
        selected: 'Selected',
        count_one: '{{count}} charge',
        count_other: '{{count}} charges',
      },
      emptyTitle: 'Nothing raised yet',
      emptyDescription:
        'Invoices and debit notes for this case appear here. A new one opens as a draft — issuing it is a separate step.',
      /* A link rather than an Edit button: an invoice is edited on its page. */
      openInvoice: 'Open the invoice',
    },

    /* The case's Invoices section (still `?section=documents`): the agreed rate,
     * the payments it is collected in, and the paper that went to the client
     * against them.
     *
     * Its own group beside `documents` rather than inside it, because they are
     * two questions on one screen: "how much is still billable" and "what has
     * actually been sent". */
    invoices: {
      rateTitle: 'Rate from the quote',
      rateSubtitle:
        'What was agreed with the client, what has been billed already, and what closes the rest.',
      fromQuote: 'from quote {{quote}}',
      rate: 'Agreed rate',
      billed: 'Billed',
      paid: 'Paid',
      headroom: 'Still billable',
      /* Colour never carries meaning alone: the bar is decoration over figures
         printed beside it, and the legend names each band in words. */
      legendPaid: 'paid by the client',
      legendAwaiting: 'billed, awaiting payment',
      legendFree: 'not billed yet',
      /* What is inside "billed". The server judges the ceiling against EVERY
         charge on EVERY invoice of the case, drafts included (`BilledOnCase`),
         and refuses the next line against that same total. A reader who assumed
         only issued paper counted would read the headroom as bigger than the
         write will allow. */
      billedCountsDrafts:
        'Drafts count as billed: the server judges the rate ceiling against every charge on every invoice of this case, and refuses the next one against the same total.',
      instalment: 'Instalment',
      expected: 'Expected',
      /* An undated tranche is not late: the second half of a 50-50 case is
         created undated on purpose, waiting for a delivery date nobody knows. */
      undated: 'no date agreed',
      notBilled: 'no invoice raised',
      /* An issued invoice removes its tranche from the registry OUTRIGHT,
         whatever it is for: the server asks "does an issued invoice name this
         tranche", not "does one cover it". So a 500.00 invoice against a
         2 000.00 instalment quietly takes 1 500.00 out of what the case
         expects. The row used to print the tranche's own figure beside that
         invoice's number, which is the one reading that is certainly wrong. */
      billedShort: '{{billed}} of {{expected}} {{currency}} billed — the rest is no longer expected',
      raiseInvoice: 'Raise invoice',
      raising: 'Opening…',
      noSchedule:
        'No payment schedule was set up, so the rate is collected in one invoice. Raising one is still fine — it simply closes no instalment.',
      /* A role with no commercial access gets no money block at all (AD-6).
         That is not "no rate agreed" — it is "we may not tell you". */
      rateWithheldTitle: 'The money on this case is not shown',
      /* The sentence this whole section was rebuilt to say: a case's costs are
         its own side of the deal and have no business on the client's invoice. */
      expensesNote:
        'Case costs do not reach this invoice: they add up to what the job cost us and are re-billed by a debit note — a separate document addressed to one counterparty.',
    },

    files: {
      title: 'Files',
      upload: 'Upload',
      takePhoto: 'Photo',
      pending: 'Pending',
      uploading: 'Sending…',
      /* The uploader of a queued file is the person reading the screen: there
         is no server row yet, so no name to resolve. */
      you: 'You',
      /* Story 8.2, UX-DR-19. The count is the point — "files are pending" is
         not something a user can check their work against, and "3 files
         waiting to send" is. */
      queuedTitle_one: '{{count}} file waiting to send',
      queuedTitle_other: '{{count}} files waiting to send',
      /* Says the queue is working, because it is. A user told only that files
         are "pending" starts pressing things. */
      queuedSending: 'They are sending now. You can leave this page — they finish on their own.',
      /* Offline. Names the guarantee explicitly, including surviving a close,
         because that is the promise that stops somebody driving back to
         re-photograph a reload. */
      queuedOffline:
        'No connection. They are saved on this device and send by themselves when the signal returns — you can close the app.',
      sendNow: 'Try now',
      dropHint: 'Drop files here, or use Upload. Photos can be taken directly on a phone.',
      uploadFailedTitle: 'A file did not send',
      /* Names the next action rather than the error. The user cannot act on
         "the PUT returned 403" and can act on "try it again". */
      uploadFailedBody:
        'Nothing was saved for it. Check the connection and add the file again.',
      emptyTitle: 'No files yet',
      emptyDescription:
        'Waybills, packing lists and reloading photos for this case appear here.',

      /* The document kind, asked once for the whole selection (0045). "What
         document is this" is a question about the PAPER, not about the file
         format: forty PDFs grouped by "PDF" are grouped by nothing. */
      kindTitle: 'What document is this',
      kind: 'Document type',
      kindHint:
        'The list is grouped by type, so the right paper is found without opening every file.',
      kindFieldHint: 'Unsure? Leave it as Other — the type is visible and can be corrected.',
      kindSubmit_one: 'Add {{count}} file',
      kindSubmit_other: 'Add {{count}} files',

      /* The type names. These are LABELS for the server's codes — 'cmr' is
         what travels on the wire, not "CMR" — because a translated grouping
         key would group one case differently for two colleagues (AD-27). */
      kinds: {
        cmr: 'CMR',
        awb: 'Air waybill',
        'rail-waybill': 'Rail waybill (SMGS)',
        'bill-of-lading': 'Bill of lading',
        invoice: 'Invoice',
        'packing-list': 'Packing list',
        'customs-declaration': 'Customs declaration',
        certificate: 'Certificate',
        contract: 'Contract',
        photo: 'Photo',
        other: 'Other',
      },

      /* Deleting. The filename goes into the accessible label because the list
         is many rows deep, and a column of buttons all called "Delete" says
         nothing about what is about to go. */
      delete: 'Delete “{{name}}”',
      deleteConfirm: 'Delete “{{name}}”? The file goes from storage too.',
      deleteConfirmAction: 'Delete',
      deleteFailed: 'The file was not deleted',
      deleteFailedHint: 'It is still there. Check the connection and try again.',
    },

    /* The case history: ONE feed over two sources — the action log and the
       financial audit trail. Nothing in the copy separates them, and nothing
       should: the question being asked is "when did this change and who did
       it", not "which of the two tables holds it".

       The `actions.*` keys repeat the machine keys in
       `backend/cmd/asa/actions.go` verbatim, with the `case.` prefix stripped:
       "case.status.change" → `actions.status.change`. The literal match is
       DELIBERATE — the feed translates the key directly and prints the raw key
       when there is no translation, so a route added to actions.go next month
       spoils one line rather than blanking the section.

       Voice follows `quotes.history.actions` below: a short passive sentence
       naming what happened, not what was clicked. "Status changed", never
       "The user changed the status" — the feed prints the actor on its own
       line, and repeating it in every title writes the name twice. */
    history: {
      title: 'History',
      count_one: '{{count}} event',
      count_other: '{{count}} events',
      /* Empty means empty across BOTH sources. The previous text promised
         financial changes only; once the action log joined the feed that
         stopped being true, so the promise widens to everything done to the
         case. */
      emptyTitle: 'Nothing has happened yet',
      emptyDescription:
        'Everything done to this case appears here — the team’s actions and the financial changes, with who and when.',
      /* The time windows. All time is the default: one case's history runs to
         tens of entries, so narrowing it is a deliberate step rather than the
         state the screen opens in. The wording matches the reporting period
         control — one choice said two different ways reads as two choices. */
      filters: {
        all: 'All time',
        today: 'Today',
        yesterday: 'Yesterday',
        week: 'This week',
      },
      searchPlaceholder: 'Search the history…',
      /* Distinct from `emptyTitle` above, and the difference is substantive: a
         case with no history has nothing to clear, this one does — and the
         text names what to clear. */
      noMatchTitle: 'Nothing found',
      noMatchDescription: 'Change the search, or pick All time to drop the time filter.',
      /* Distinct from `emptyTitle` again, and for the same reason the skeleton
         waits for both sources: with one of them refused and the other holding
         nothing, "Nothing has happened yet" is a claim about a history only
         half of which was read. Unknown is not empty. */
      unknownTitle: 'The history could not be read in full',
      unknownDescription:
        'One of the two sources did not answer and the other holds nothing, so whether anything has happened to this case is unknown. Retry above.',
      /* One source failing does not blank the section: the other renders as it
         is, and a notice names what is missing from the feed. Half a history
         that says nothing reads as the whole history — which is how "nobody
         ever changed that" gets said out loud. */
      loadFailed: {
        actions: 'The action log could not be loaded — the feed shows financial changes only.',
        audit: 'The financial changes could not be loaded — the feed shows the team’s actions only.',
        retry: 'Retry',
      },
      /* The feed does not paginate and BOTH its sources are capped: the trail
         at the limit this client asks for, the action log at the one the server
         imposes on a read it takes no limit for. When a cap bites, say so out
         loud — a truncated feed that stays quiet looks like a complete one.
         Each source gets its own sentence, because one cap biting says nothing
         about the other. The numbers are interpolated rather than spelled here,
         so that raising a cap cannot leave a sentence quietly stating the old
         one. */
      truncated: {
        actions: 'Showing the most recent {{limit}} recorded actions.',
        audit: 'Showing the most recent {{limit}} financial changes.',
      },
      audit: {
        reason: 'Reason',
      },
      actions: {
        create: 'Case created',
        update: 'Case details updated',
        status: {
          change: 'Status changed',
        },
        reopen: 'Case reopened',
        leg: {
          add: 'Leg added',
          update: 'Leg updated',
          remove: 'Leg removed',
        },
        /* Counterparties are the external sides of the shipment; the team
           below is ASA staff. The pair sits together because these two are
           what get confused, and the words carry the difference rather than
           the order of the keys. */
        participant: {
          add: 'Counterparty added',
          update: 'Counterparty updated',
          remove: 'Counterparty removed from the case',
        },
        team: {
          add: 'Colleague added to the team',
          remove: 'Colleague taken off the team',
        },
        /* `remove` will never appear in a case's feed: deleting a file is
           logged under the "file" entity, not "case". The translation is here
           anyway — it costs one line and spares the next reader the conclusion
           that the dictionary is incomplete. Same for `status-update.send-bulk`
           below. */
        file: {
          attach: 'File attached',
          remove: 'File deleted',
        },
        /* Two keys, one string, and not by accident. Both entries mean the same
           thing: somebody wrote in the case conversation. Inventing separate
           titles would show the reader a distinction they cannot act on. */
        message: {
          post: 'Message posted to the chat',
        },
        chat: {
          post: 'Message posted to the chat',
        },
        /* The bulk send names its cases in the body rather than the path, so
           the entry carries no case id and lands in no feed. Translated for
           the same reason as `file.remove`. */
        'status-update': {
          send: 'Status update sent to the client',
          'send-bulk': 'Status update sent across several cases',
        },
        rate: {
          set: 'Rate set',
        },
        expense: {
          record: 'Cost line added',
          update: 'Cost line updated',
          delete: 'Cost line removed',
        },
        payment: {
          record: 'Payment recorded',
        },
        invoice: {
          raise: 'Invoice draft created',
          update: 'Invoice updated',
          line: {
            add: 'Invoice line added',
            update: 'Invoice line updated',
            delete: 'Invoice line removed',
          },
          render: 'Invoice document generated',
        },
        tranche: {
          date: {
            set: 'Tranche date set',
          },
        },
      },
    },

    chat: {
      label: 'Case chat',
      placeholder: 'Message the case team',
      send: 'Send message',
      heading: 'Chat',
      internalOnly: 'Internal only',
      you: 'You',
      sending: 'Sending…',
      latest: 'Latest',
      /* Story 8.3. The conversation is read a page at a time and polled while
         visible, so these describe states an always-loaded fixture never had:
         a first page in flight, a page-back, and messages that landed while
         somebody was reading further up. */
      loading: 'Loading conversation',
      failed: 'The conversation could not be loaded.',
      empty: 'Nothing said yet. Start the thread.',
      earlier: 'Earlier messages',
      loadingEarlier: 'Loading…',
      /* The jump-to-latest pill once something has arrived below the reader.
         It states the count because "3 new" and "1 new" are different things
         to walk back into. */
      unread_one: '{{count}} new message',
      unread_other: '{{count}} new messages',
      /* The phone badge's accessible name. It replaces the plain label rather
         than being appended to it, so a screen reader hears the count as part
         of what the control is for rather than as trailing decoration. */
      openWithUnread_one: 'Case chat, {{count}} new message',
      openWithUnread_other: 'Case chat, {{count}} new messages',
      messageLabel: 'Message',
      sendHint: 'Enter to send · Shift + Enter for a new line',
      attach: 'Attach files',
      removeAttachment: 'Remove {{name}}',
      dropHere: 'Drop files to attach',
      tooLarge: '{{name}} is larger than 50 MB and was not attached.',
      tooMany: 'A message can carry {{count}} files.',
      /* The day separators between runs of messages. Two named days and then
         a formatted date, because "Today" and "Yesterday" are what a reader
         checks a coordination thread against — an absolute date for something
         said four hours ago makes them do arithmetic to learn it is current. */
      today: 'Today',
      yesterday: 'Yesterday',
      /* The record dock (FR-46, UX-DR-13, 0052). Its own group because these
         describe the CONTAINER — its title bar and its three controls — while
         everything above describes the conversation inside it, which is the
         same conversation the chat page renders. */
      dock: {
        case: 'Case chat',
        quote: 'Quote chat',
        /* The record the panel is about, under its title. The separator is a
           middle dot with hair spaces around it, as everywhere else. */
        subject: '{{number}} · {{client}}',
        internal: 'Internal',
        teamOnly: 'Visible only to your team',
        minimise: 'Minimise chat',
        expand: 'Open the full chat page',
        /* The collapsed control: a floating button with a label on hover. It
           says INTERNAL chat rather than naming the record, because the record
           is the page behind it and repeating it would be noise. */
        open: 'Internal chat',
        /* Replaces the plain label rather than trailing it, so a screen reader
           hears the count as part of what the control is for. */
        openWithUnread_one: 'Internal chat, {{count}} new message',
        openWithUnread_other: 'Internal chat, {{count}} new messages',
      },
    },
  },

  /* The chat page (0029): conversations across records, in two tabs.
     
     A top-level namespace rather than more keys under `cases`, because the
     page spans quotes AND cases and nesting it under either would misname
     half of what it shows. The panel's own strings stay at `cases.chat.*`:
     they are the same words in both places, and moving them would touch every
     call site in ChatPanel for no gain the reader can see. */
  chat: {
    title: 'Chat',
    subtitle: 'Conversations on the work you are on.',
    tabs: {
      cases: 'Cases',
      quotes: 'Quotes',
    },
    /* The list beside the panel. `empty` is per tab: a reader with no quote
       conversations has not lost anything, they simply have no quotes. */
    emptyCases: 'No case conversations yet.',
    emptyQuotes: 'No quote conversations yet.',
    /* Nothing is selected, which is the first thing the page shows on a wide
       screen. Not an error, so it reads as an invitation. */
    pickOne: 'Choose a conversation to read it.',
    loading: 'Loading conversations',
    failed: 'The conversations could not be loaded.',
    /* The unread pill on a list row. Capped at 99 by the server, so the
       hundredth is "99+" rather than a number nobody reads to the end of. */
    unread_one: '{{count}} unread',
    unread_other: '{{count}} unread',
    unreadCapped: '99+',
    /* A converted quote's thread. Read-only, and the banner replaces the
       composer rather than disabling it — a disabled box cannot explain
       itself, and this refusal has a reason worth reading. */
    archived: 'Archive — read only',
    archivedWhy: 'This quote became a case. Its conversation continues there.',
    /* Editing and deleting (0029). The marker is permanent: 0023 refused
       editing outright because a silent rewrite is worse than no message, and
       what makes it acceptable is that it is never silent. */
    edited: 'edited',
    deletedBody: 'Message deleted',
    edit: 'Edit',
    editing: 'Editing',
    saveEdit: 'Save',
    cancelEdit: 'Cancel',
    delete: 'Delete',
    confirmDelete: 'Delete this message? It stays in the thread as a note that something was removed.',
    editFailed: 'The message could not be edited.',
    deleteFailed: 'The message could not be deleted.',
    /* The quote side has no paperclip: there is no quote file store, and a
       message must never carry its own bytes (0023). */
    noAttachmentsOnQuotes: 'Files attach to cases, not to quotes.',

    /* The list's own filter (0052). Client-side over the rows already held —
       the server sends every conversation this reader may open in ONE
       response, so narrowing it is a substring test rather than a request.
       The placeholder is per tab because the list is per tab: offering to
       search cases on the quote tab would describe rows that are not there. */
    search: {
      cases: 'Search cases…',
      quotes: 'Search quotes…',
      /* The accessible name. The placeholder is not one — it disappears the
         moment somebody types, which is exactly when a screen reader user
         would want to be told what the box is. */
      label: 'Search conversations',
      /* A filter that matched nothing. Distinct from `emptyCases` above: the
         reader HAS conversations, this text does not name any of them. */
      none: 'No conversation matches that.',
    },
    /* The last line of a conversation, on its row. Author and body in one
       string rather than two spans, so a translator can move the colon — and
       so a right-to-left language would not need a second layout. */
    preview: '{{author}}: {{body}}',

    /* The thread's header. It names the record, because the list beside it can
       be scrolled away from the selected row and a thread with no title is an
       unexplained column of bubbles. */
    threadSubtitle: {
      case: '{{client}} · Case conversation',
      quote: '{{client}} · Quote conversation',
    },
    /* The same subtitle for a record whose client the two lists have not
       resolved yet — better a shorter true line than "undefined · …". */
    conversationKind: {
      case: 'Case conversation',
      quote: 'Quote conversation',
    },
    /* Internal, always, and said twice on purpose: a chip beside the title for
       the reader who already knows, and a banner for the one who does not.
       External participants are structurally absent from this surface
       (FR-46) — nothing typed here can reach a client. */
    internal: 'Internal',
    internalBanner: 'Internal conversation — visible only to your team',
    /* Out of the conversation and onto the thing it is about. A link rather
       than an action, so it opens in a new tab the ordinary way. */
    viewCase: 'View case',
    viewQuote: 'View quote',
  },

  quotes: {
    title: 'Quotes',
    subtitle: 'What we have offered, and what it earns if it lands.',
    loading: 'Loading quote',
    backToList: 'Back to quotes',
    /* The count in the table's footer. It counts what is visible AFTER the
       filter: the footer sits under that list and has to answer for it, not
       for a set the screen decided not to show. */
    count_one: '{{count}} quote',
    count_other: '{{count}} quotes',

    /* The five payment options from the source workbook's Reference list. The
       VALUE stored and printed is the English string; these are what our own
       colleague reads in the picker. */
    paymentOptions: {
      'in-advance-payment': 'Paid in advance',
      '50-50-payment-upon-pickup-delivery': '50/50 — on pickup and on delivery',
      'full-payment-upon-delivery-before-unloading':
        'Paid in full on delivery, before unloading',
      credit: 'On credit terms',
      other: 'Other — stated in the terms below',
    },

    /* Win/loss over a date range (FR-15). WHOSE numbers these are is part of
       reading them: a Sales user's figures cover their own quotes, and a
       personal number read as a company number is the failure AC 6 guards
       against — so the caveat is a permanent part of the widget, never a
       tooltip. Expired keeps its own count and is never folded into Lost: a
       quote nobody answered is a different failure from one the client
       refused. */
    statistics: {
      title: 'Win / loss',
      won: 'Won',
      wonHint: 'Converted to a case',
      lost: 'Lost',
      lostHint: 'Rejected',
      expired: 'Expired',
      expiredHint: 'Counted on its own, never as lost',
      conversion: 'Conversion',
      /* Names the denominator rather than describing it. "Won of decided"
         leaves the reader to work out whether expired quotes are in it; the
         counts say so outright, and they are the same three figures standing
         beside this one. */
      conversionHint: '{{won}} of {{decided}} decided',
      wonValue: 'Won value',
      scope: {
        own: 'Your quotes only',
        assigned: 'Quotes you own or are on',
        all: 'Every quote, all owners',
      },
      range: {
        label: 'Period',
        d30: 'Last 30 days',
        d90: 'Last 90 days',
        year: 'This year',
        all: 'All time',
      },
      /* The days the server counted, printed on the panel's header line after
         the scope caveat: the control names the period that was asked for, this
         names the one that came back. Bare dates, because the line already
         reads as a caption — a "counted over" prefix repeated the obvious in
         the one place on the panel where every character is beside a heading. */
      period: '{{from}} – {{to}}',
      error: 'The win/loss figures could not be read.',
    },

    /* The detail page's section switcher. The labels name the same things the
       card headers used to, because a reader who learned "Cost lines" as a
       heading should not have to learn it again as "Pricing". */
    sectionsLabel: 'Quote sections',
    tabs: {
      cargo: 'Cargo',
      pricing: 'Cost lines',
      routes: 'Routes',
      participants: 'Participants',
      terms: 'Terms',
      revisions: 'Revisions',
      history: 'History',
    },

    actions: {
      newQuote: 'New quote',
      editQuote: 'Edit quote',
      /* The way back into the three-step builder from a saved quote. Named for
         what it RESUMES rather than for the screen it opens ("Open the
         builder"), because the reader who needs it has already been there once:
         they created the quote in it, walked away, and came back for the markup
         it is the only home of. "Edit quote" beside it is the header sheet and
         a different, smaller act — the two labels have to stay tellable apart. */
      create: 'Create quote',
      approve: 'Client approved',
      converting: 'Creating case…',
      openCase: 'Open case {{number}}',
      newRevision: 'New revision',
      reopening: 'Opening…',
      sendOffer: 'Send offer',
      preview: 'Preview',
      fixCargo: 'Fix cargo',
    },

    /* The parties on the shipment (FR-24), on the quote's own page — never on
       the way in: a quote does not name its external parties, so the create form
       does not ask, and this vocabulary belongs to a record that already exists.
       Its own block rather than a reuse of `cases.participants`: the two
       surfaces ask at different moments and say different things about what
       happens next. A case's participants are told about a shipment that is
       running; a quote's are recorded before it is won, and nothing is sent to
       them from here. Sharing the strings would have made the case's
       "external parties are reached by email only" a promise this screen does
       not keep. */
    participants: {
      add: 'Add participant',
      role: 'Role',
      contactHint: 'Email or phone. Optional.',
      notifyByEmail: 'Notify by email',
      /* States plainly that the tickbox records a preference and sends nothing
         yet. Without this, someone ticks it expecting the offer to be copied to
         the consignee — which it is not; the offer goes to the client. */
      notifyHint:
        'Records that this party may be emailed. Carried onto the case, where status updates use it. The offer itself goes to the client.',
      contactNeededForEmail: 'Add an email address to notify this party.',

      /* The rest of the detail page's section: the vocabulary a SAVED record
         has — editing, removing, and the state of an existing row. The keys
         above used to be shared with a card on the builder's step 1, which is
         gone; they stay in the same order because the seven parties are still
         asked for in these words, now on the case as well. */
      edit: 'Edit participant',
      count_one: '{{count}} participant',
      count_other: '{{count}} participants',
      notifiedByEmail: 'Notified by email',
      noContact: 'No contact',
      emptyTitle: 'No participants yet',
      emptyDescription:
        'Shippers, consignees, agents and drivers on this shipment appear here.',
      delete: 'Remove from quote',
      /* Names the consequence rather than asking "are you sure": what is lost
         is the record of who was on the shipment, and the offer does not change
         because the parties are not printed on it. */
      deleteConfirm: 'Remove this party from the quote?',
      deleteConfirmAction: 'Remove',
    },

    /* The INTERNAL team on a quote (migration 0028) — colleagues, not parties.
       Its own block sitting deliberately next to `participants` above, because
       the pairing is exactly where the confusion lives: those are external
       people on the shipment (a shipper, a driver), these are ASA staff with
       logins. The copy has to carry that distinction on its own, because the
       two are asked for on different screens now — the team on the way in, the
       parties on the quote once it exists — and "add somebody" reads the same
       either way.

       Kept separate from `cases.team` for the reason the participants blocks
       are separate: a case is running work and a quote is not yet won, so
       "they can see this" means something different on each. */
    team: {
      title: 'Team',
      /* Says what adding somebody DOES, because on a quote it is not cosmetic:
         membership is what grants visibility. Optional is stated for the same
         reason it is on participants — most quotes are worked by one person. */
      hint: 'Colleagues who work this quote with you. Adding somebody lets them see it. Optional.',
      empty: 'Only you for now. Add a colleague if somebody else is working this deal.',
      add: 'Add colleague',
      colleague: 'Colleague',
      /* Names the person, not the position: unlike a participant row, a
         colleague is picked rather than typed, so their name always exists by
         the time this label is read. */
      remove: 'Remove {{name}} from the team',
      /* The owner is on the team from the moment the quote is created and is
         the one member who cannot be taken off. */
      owner: 'Owner',
      you: 'You',
      /* Shown where the picker would otherwise be empty. Distinguishes "nobody
         else works here" from "the list failed to load", which is the reading a
         bare empty dropdown invites. */
      noneAvailable: 'No other colleagues to add.',
      /* The two refusals the server can answer with, in the words the user can
         act on. */
      lastMember: 'Assign another colleague before removing the last one.',
      notAssignable: 'That account cannot be assigned.',
    },

    /* The stepped builder (UJ-1), which replaced the new-quote modal. The step
       labels name the WORK, and the hint under each says what belongs there —
       "Price and send" alone does not settle whether the agent's price is
       entered on that step or on the routing one. */
    /* Opening a quote (UJ-1, FR-4, FR-5).

       What is left of a three-step builder. The steps are gone because each one
       had a twin on the detail page; this asks only what the create payload
       carries, and everything else is answered on the record itself. */
    create: {
      title: 'New quote',
      subtitle: 'Who the offer is for. Everything else is filled in on the quote itself.',
      identity: 'Client and cargo',
      identityHint: 'Who the offer is for, and what is moving.',
    },
    /* The read-only cards beside both quote screens. Nothing here is the only
       home for a fact — every line of it is also on a card in the main column,
       which is what makes the rail droppable on a narrow screen. */
    aside: {
      quote: 'Quote',
      number: 'Number',
      /* The number is minted server-side (FR-5). Said in words, so nobody hunts
         for a field that does not exist. */
      numberPending: 'assigned on create',
      status: 'Status',
      owner: 'Owner',
      readiness: 'Offer readiness',
      readyIdentity: 'Client and contact are in',
      readyRouting: 'Route and cargo are in',
      readyPricing: 'The price is costed',
      offerCarries: 'The offer will carry',
      /* The dark money card. Its three figures are labelled from the costing
         and markup dictionaries — `quotes.costLines.customerTotal`,
         `quotes.pricing.cost`, `quotes.pricing.marginOverRevenue` — because the
         same values stand under the same words on the costing tab. A second
         dictionary for one value is two values to anybody reading quickly. */
      money: 'The money on this quote',
      /* Shown only while the costing is empty: three zeroes with nothing said
         about them look like a card that failed to load. */
      moneyNote:
        'No cost lines yet — the figures appear here as soon as the costing is filled in.',
    },

    /* Sending the offer (FR-71). The sheet exists to show the address before an
       email leaves permanently — not to ask for anything unnecessary. */
      /* The offer preview (UX-DR-15), shown before the irreversible send.

         The document's OWN labels are not here: they come from the server in
         the client's language, because that is the language the PDF prints in.
         These strings are the sheet's chrome — ours, in the user's language. */
      preview: {
        title: 'Offer preview',
        loading: 'Composing the offer',
        close: 'Close',
        /* Printed where the issued document prints its number. A preview has no
           number because minting belongs to the send (AD-12), and DRAFT is the
           same word the PDF renderer uses for an empty one. */
        draft: 'DRAFT',
        revision: 'revision {{label}}',
        signature: 'Signature and stamp',
        /* The DOCUMENT's language, not the interface's — which is why the
           control is labelled this way rather than "Language". The offer is
           written for the client, and a salesperson working in Russian still
           has to be able to read the English page before it goes out. */
        language: 'Document language',
        /* Says which way round the switch works, because the sheet's chrome
           does not move with it: the tables and field labels below are the
           client's, the buttons around them stay the reader's. */
        languageHint: 'The language the client will read this in. The send uses the same one.',
        /* The offer cannot go yet, and the document below is still accurate —
           the banner says the first without contradicting the second. */
        blockedTitle: 'This cannot be sent yet',
        blockedNoCargo: 'The offer has no cargo. Add the positions on step 2 — an offer with nothing on it prices nothing.',
        blockedChecklist: 'The cargo checklist is still open. Step 2 names what is missing.',
        blockedNotDraft: 'This quote has already been issued. Open a new revision to send it again.',
        blockedNoRecipient: 'This client has no contact with an email address. Add one in the directory.',
        blockedGeneric: 'The server will not issue this offer yet.',
      },
    offerSend: {
      title: 'Send offer',
      document: 'Document',
      nextRevision: 'revision {{label}}',
      validUntil: 'Valid until {{date}}',
      recipient: 'Recipient',
      noRecipient: 'This client has no contact with an email address. Add one in the directory — there is nowhere to send the offer without it.',
      /* The FR-64 refusal is NOT here any more: it is `errors.branding`, because
         the offer is no longer the only document it can refuse. */
      explainer: 'The offer is saved as an immutable revision, the PDF is filed against the quote, and the client receives an email linking to the document.',
    },

    /* The new-quote sheet takes the header only. Cargo and cost lines are
       entered on the quote itself, where the checklist watches them. */
    form: {
      origin: 'Origin',
      destination: 'Destination',
      mode: 'Mode',
      incoterm: 'Incoterm',
      /* Our sentence about the rule, not the rule's own name — the ICC terms
         themselves stay English in both catalogues, because "Free On Board"
         translated appears on no bill of lading. */
      incotermSeaOnly: 'sea and inland waterway only',
      quoteDate: 'Quote date',
      paymentTerms: 'Payment terms',
      transitDays: 'Transit time the client asked for, in days',
      transitDaysMin: 'Fewest days',
      transitDaysMax: 'Most days',
      transitInverted: 'The shortest transit cannot be longer than the longest.',
      /* The nine FR-4 / FR-11 / FR-59 header fields that had no entry path in
         any quote surface until 2026-08-17. Every one is a column on `quotes`
         that the DTO already served and no form could fill. */
      clientContact: 'Contact at the client',
      /* Names the person the offer will actually go to when none is picked,
         because "optional" alone leaves the reader wondering whether an
         unaddressed offer sends at all. */
      clientContactDefault: 'Defaults to {{name}}',
      cargoOwner: 'Cargo owner',
      /* A placeholder, where the label used to be followed by "Optional". It
         answers the question that word never did — what a good answer looks
         like — and it costs the field no height. */
      cargoOwnerPlaceholder: 'If it is not the client',
      cargoDescription: 'Cargo description',
      cargoDescriptionPlaceholder: 'Marble slabs, 15 pallets',
      pickupLocation: 'Pickup location',
      dealTermsLegend: 'Deal terms',
      demurrageTerms: 'Demurrage after free time',
      demurrageTermsHint: 'As agreed, in words — e.g. USD 80/day per container.',
      deliveryTerms: 'Delivery terms',
      /* Says what it is NOT, because the field above it in the same sheet is the
         Incoterm and the two are easy to conflate. */
      deliveryTermsHint: 'How delivery happens in practice — not the Incoterm.',
      carrierDisplayLegend: 'Carrier line on the offer',
      carrierDisplay: 'Carrier or supplier, as printed',
      /* The second clause of Story 2.1 AC 2, said where somebody might otherwise
         use this as a vendor field. */
      carrierDisplayHint:
        'Printed on the offer for information only. Assigns no vendor to any cost line.',
      validUntilHint: 'After this date the offer shows as expired.',
      measures: 'Weight and volume',
      grossWeightOverride: 'Gross weight kg',
      volumeOverride: 'Volume CBM',
      overrideHint:
        'Leave blank to use the total of the cargo positions. A figure here replaces it on the offer.',
      chargeableWeightHint: 'For the whole consignment, when the carrier gives one figure.',
      finalPackingList: 'Final packing list received',
      finalPackingListHint:
        'The volumes above are the client’s rather than our estimate. Stays visible on the case.',
      nextStep: 'Cargo and cost lines are added on the quote itself.',
    },
    columns: {
      number: 'Quote',
      client: 'Client',
      route: 'Route',
      /* The header's one-line cargo description, headed by the short word: the
         column is narrow and "Cargo description" would wrap the heading over a
         cell that is itself one truncated line. */
      cargo: 'Cargo',
      status: 'Status',
      validUntil: 'Valid to',
      sell: 'Sell USD',
      margin: 'Margin USD',
    },
    /* The board's drag receipt: what moved and where, in one clause, with the
       undo beside it. */
    board: {
      moved: '{{number}} moved to {{status}}',
      /* An empty lane, in words. The invitation is a SECOND key because it is
         only true of a lane something may actually be dragged into — printing
         it over Converted would teach a gesture that lane refuses. */
      laneEmpty: 'No quotes',
      laneDrop: 'Drag one here to change its status.',
    },

    card: {
      /** The issued-version marker beside the date: "Rev A · 26 Jul". */
      rev: 'Rev {{label}}',
      total: 'Total',
      margin: 'Margin',
      validTo: 'Valid to',
      /** Client silence on an issued quote, in the words used at standup. */
      noAnswer: 'no answer {{count}} d',
      /** A mixed quote counts positions rather than naming one package type. */
      positions_one: '{{count}} position',
      positions_other: '{{count}} positions',
      /** What blocks the send. The card counts; the quote lists (FR-3). */
      blocked_one: '{{count}} position incomplete',
      blocked_other: '{{count}} positions incomplete',
    },
    emptyTitle: 'No quotes yet',
    emptyDescription:
      'Quotes you create appear here with their margin and status.',

    cargo: {
      title: 'Cargo',
      add: 'Add position',
      edit: 'Edit position',
      position: 'Position',
      hsCode: 'HS code',
      packages: 'Packages',
      packageType: 'Package type',
      weight: 'Weight kg',
      volume: 'Volume CBM',
      /* The counter-signal: a 50 CBM declaration that arrives as a 90 CBM
         truck. Volume is required, and the message says what it blocks. */
      /* Stands in for the number in the cargo table: not measured yet. */
      missing: 'Missing',
      volumeRequired: 'Required — without it the offer cannot be sent',
      volumeHint: 'Checked against the booking before the offer goes out.',
      /* Multi-shipper loadings are routine (FR-12): three shippers' boxes on
         one truck, and the offer has to say which are whose. */
      shipper: 'Shipper',
      shipperHint: 'Only when this position ships from someone else.',
      /* Entered, never computed — the volumetric divisor is the carrier's. */
      chargeableWeight: 'Chargeable weight kg',
      chargeableWeightHint: 'Only when the carrier bills something other than the gross weight.',
      totals: 'Total',
      overriddenMark: 'edited',
      overriddenFrom: 'Manually set. The positions add up to {{value}}.',
      partialTotal: 'Some positions have no figure entered, so this total is incomplete.',
      packingListReceived: 'Final packing list received',
      dimensions: 'Dimensions cm',
      lengthCm: 'Length',
      widthCm: 'Width',
      heightCm: 'Height',
      isChemical: 'Chemical cargo',
      chemicalNote:
        'Chemical cargo needs an MSDS and a transport certificate before the offer can be sent.',
      hasMsds: 'MSDS received',
      hasTransportCertificate: 'Transport certificate received',
      chemical: 'Chemical',
      readyTitle: 'Ready to send',
      readyDescription: 'Cargo is complete and the offer can go out.',
      emptyTitle: 'No cargo yet',
      emptyDescription:
        'Add the positions this quote covers. The offer cannot be sent until they are complete.',
      delete: 'Remove this position',
      deleteConfirm: 'Remove this position? This cannot be undone.',
      deleteConfirmAction: 'Remove',
    },

    /* The checklist counts what stands between a draft and a sent offer. */
    checklist: {
      blocked_one: '{{count}} thing blocks this offer',
      blocked_other: '{{count}} things block this offer',

      /* One line of the list: what is missing and what it blocks. Joined here
         rather than in code — the dash and the order of the parts are the
         language's business. */
      line: '{{issue}} — {{blocks}}',

      /* What is missing. The position is named by the number the user counts
         by, because this is read next to a numbered table. The unnumbered form
         covers a row that has no number yet. */
      issue: {
        dimensionsMissing: 'Dimensions missing on position {{position}}',
        dimensionsMissing_noPosition: 'Dimensions missing',
        volumeMissing: 'Volume missing on position {{position}}',
        volumeMissing_noPosition: 'Volume missing',
        msdsMissing: 'MSDS missing on position {{position}}',
        msdsMissing_noPosition: 'MSDS missing',
        certificateMissing: 'Transport certificate missing on position {{position}}',
        certificateMissing_noPosition: 'Transport certificate missing',
      },

      /* What it blocks. The chemical reason is named, or it is not clear why
         this position is held to a stricter rule than the one above it. */
      blocks: {
        send: 'Cannot send the offer',
        sendChemical: 'Cannot send the offer — cargo is chemical',
      },
    },

    routes: {
      title: 'Route options',
      transitDays_one: '{{count}} day',
      transitDays_other: '{{count}} days',
      /** Compact forms for the A/B/C rows, where the full sentence crowds. */
      daysShort: 'd',
      transitUpTo: 'up to {{count}} d',
      transitFrom: 'from {{count}} d',
      emptyTitle: 'No route options yet',
      emptyDescription: 'Routings you price appear here for the client to choose from.',
      add: 'Add option',
      /* The dashed tile in the empty grid slot. Names what the routing would be
         FOR rather than the act, because the act is already named by the button
         in the card header — and two controls with one name are two controls a
         screen reader cannot separate. */
      addNext: 'Option {{letter}} — for the client to compare',
      edit: 'Edit option',
      origin: 'Origin',
      /** FR-19: kept as its own field so conversion never re-parses a string. */
      borderCrossing: 'Border crossing',
      destination: 'Destination',
      mode: 'Mode',
      indicativeTotal: 'Indicative total, USD',
      indicativeHint:
        'Shown on options B and C. The selected option is priced by its cost lines.',
      selected: 'Selected',
      select: 'Select',
      /* The FR-3 repricing warning. The server refuses the silent move; this
         is the question it asked. */
      selectWarning:
        'The cost lines price the currently selected option. After the switch they will describe this routing — check them before the offer goes out.',
      selectConfirm: 'Switch selection',
      delete: 'Delete option',
      deleteConfirm: 'Delete this route option? The offer will no longer show it.',
      deleteConfirmAction: 'Delete',
      /* FR-3: the selected option holds the costing. An instruction, not an
         apology — the fix is one select away. */
      deleteHoldsCosting:
        'This option is selected and the cost lines price it. Select another option first — a quote is never left priced against nothing.',
      deleteFailed: 'The option could not be deleted. Try again.',
    },

    terms: {
      title: 'Terms',
      /* The card's subtitle while it can be edited in place. It says where the
         change lands, because otherwise it is unclear whether it rewrites an
         offer the client already has. */
      editHint: 'Changes apply to the next offer. Revisions already issued do not move.',
      days_one: '{{count}} day',
      days_other: '{{count}} days',
    },
    /* The third block of the reference quotation: IMPORTANT NOTES.

       "Note", not "disclaimer", and the difference is the reader's. A disclaimer
       is a lawyer's word for a paragraph that limits liability; what actually
       goes here is "free waiting time is normally two days at destination" —
       and the paper it prints on calls that a note. The stored kind is still
       `disclaimer`, because that is the word the contract and the templates use
       (0032); only the screen changed. */
    notes: {
      title: 'Notes',
      hint: 'Printed on the offer after the price.',
      frozen: 'Printed on the offer that was sent. A sent quote shows what the client was given.',
      empty: 'No notes yet.',
      /* The placeholder is the instruction — there is no label above the box,
         because the card heading already said what these are. */
      addPlaceholder: 'Add a note…',
      add: 'Add',
      edit: 'Edit this note',
      remove: 'Delete this note',
    },
    /* What the offer says the price covers and does not (Story 5.2, FR-60).
       These are words handed to a client, so the copy here describes what will
       be on the paper rather than what is in the database — "prints", not
       "saves". The headings match the settings templates the text is usually
       copied from, so a reader recognises them.

       The notes that used to be the third list here are `quotes.notes` now,
       on their own card. */
    blocks: {
      title: 'Included & not included',
      hint: 'Printed on the offer after the price: what the quote covers and what it does not.',
      frozen: 'Printed on the offer that was sent. A sent quote shows what the client was given.',
      /* The inclusions lead, because that is the order the offer prints them
         in. "Inclusion" as the noun and "Included" as the heading: the heading
         is the client's word, printed above the list on the paper. */
      included: 'Included',
      includedEmpty: 'Nothing listed as included yet.',
      addIncludedPlaceholder: 'Add an inclusion…',
      addIncluded: 'Add an inclusion',
      editIncluded: 'Edit this inclusion',
      removeIncluded: 'Delete this inclusion',
      notIncluded: 'Not included',
      notIncludedEmpty: 'Nothing excluded yet.',
      addNotIncludedPlaceholder: 'Add an exclusion…',
      addNotIncluded: 'Add an exclusion',
      editNotIncluded: 'Edit this exclusion',
      removeNotIncluded: 'Delete this exclusion',
      delete: 'Delete',
      capped:
        'This quote already carries as many entries of that kind as an offer can print. Delete one to make room.',
      loadFailed: 'These could not be loaded. Reload the page to try again.',
    },
    /* The action history (the History tab): the global action log's entries
       for this quote, translated from their machine keys — the server stores
       "quote.cargo.create", never a sentence (see QuoteDetailPage's
       ACTION_LABELS). Past tense throughout: these are facts about what
       happened, not buttons. */
    history: {
      title: 'History',
      emptyTitle: 'Nothing yet',
      emptyDescription: 'Every action on this quote is recorded here.',
      actions: {
        created: 'Quote created',
        updated: 'Quote details updated',
        cargoAdded: 'Cargo position added',
        cargoUpdated: 'Cargo position updated',
        cargoRemoved: 'Cargo position removed',
        costLineAdded: 'Cost line added',
        costLineUpdated: 'Cost line updated',
        costLineRemoved: 'Cost line removed',
        routeOptionAdded: 'Route option added',
        routeOptionUpdated: 'Route option updated',
        routeOptionSelected: 'Route option selected',
        routeOptionRemoved: 'Route option removed',
        blockAdded: 'Offer text block added',
        blockUpdated: 'Offer text block updated',
        blockRemoved: 'Offer text block removed',
        sent: 'Offer sent to the client',
        reopened: 'Reopened for a new revision',
        answerApproved: 'Client approved',
        answerRejected: 'Client rejected',
        answerRecorded: 'Client answer recorded',
        converted: 'Converted to a case',
      },
    },
    revisions: {
      title: 'Revisions',
      emptyTitle: 'No revisions yet',
      emptyDescription: 'Each issued offer is kept here as it was sent.',
      /* Why the button is off. The status is the reason, so the sentence names
         it rather than a bare refusal. */
      notReopenable: 'A new revision can only be opened on a sent or rejected quote.',
      /* Why the header cannot be typed into. Says what to DO — an issued quote
         changes by revision and never in place (FR-7), so the next act is the
         button beside this one, not a retry of this one. */
      notEditable:
        'A sent quote is edited by opening a new revision, not in place.',
    },
    costLines: {
      title: 'Cost lines',
      add: 'Add line',
      edit: 'Edit line',
      description: 'Description',
      vendor: 'Vendor',
      /* The hint under the vendor field. It says exactly what migration 0019
         decided: the directory suggests here, it does not constrain. */
      vendorHint: 'Pick from the directory, or type your own',
      vendorPlaceholder: 'Start typing a name',
      vendorNoMatches: 'No counterparty by that name',
      vendorFreeTextHint: 'The name you typed is kept as it is',
      qty: 'Qty',
      quantity: 'Quantity',
      /* The FIRST of the two money columns, and it is the unit COST — the figure
         the operator types, exactly as in the source workbook's INTERNAL COST
         AND SELLING block. The customer's price is that figure carried up by
         the markup (quotes.pricing below), so it is derived and never typed.

         "Unit price" rather than "Cost" because that is the workbook's own
         column heading and the word the operator uses for what they enter; the
         panel that reads the cost side back calls its total "Cost", which is a
         different figure under a different name on purpose. */
      unitPrice: 'Unit price',
      /* The SECOND money column: the unit price carried out by the quantity,
         which is the workbook's own "amount" beside its "unit cost".

         Both single-column escapes are defective, which is why there are two.
         One column headed "Unit price" printing the extension is a heading that
         lies about the cell beneath it; one column headed "Amount" holding the
         input puts the figure an operator types under the word for a figure
         nobody types. Read-only here in both the read row and the open one — it
         is arithmetic on the two cells to its left, never an entry. */
      amount: 'Amount',
      /* Rate memory REPORTS; it never pre-fills (FR-17). Accepting it puts the
         remembered figure in the unit price field, where it can be read and
         overtyped before anything is saved. */
      rateMemory: 'Last on this lane: {{amount}} · {{case}} · {{month}}',
      useRate: 'Use',
      /* The footer, in the workbook's order: what the quote costs, what is
         added to it, what the customer pays. These three words are the whole
         vocabulary of this surface and they are used unchanged on the phone
         list, in the wizard's header and in the markup panel — a figure called
         "Buy" in one place and "Total cost" in another is two figures as far as
         a reader is concerned.

         "Total cost" rather than the old "Totals": the row states ONE side of
         the quote, and a row called "Totals" sitting above a row called
         "Margin" reads as the sum of everything rather than as the first half
         of the sum. */
      costTotal: 'Total cost',
      margin: 'Margin',
      customerTotal: 'Customer total',
      delete: 'Remove line',
      deleteConfirm: 'Remove this line and its share of the margin?',
      deleteConfirmAction: 'Remove',
    },

    /* The pricing desk (FR-1, FR-49, FR-69): asking a colleague for a number,
       and pricing the costing once it is there.

       Two separate acts under one heading because they are the two halves of the
       same question — who decides this price, and what it comes to. The hand-off
       is a REQUEST rather than a staffing: "you were added to this quote" is
       information, "somebody is waiting for your price" is work, and the server
       publishes them as two topics for that reason. */
    pricing: {
      desk: 'Pricing',
      deskHint:
        'Who is deciding this price. Adding a colleague here asks them for a number and lets them see the quote.',
      colleague: 'Colleague',
      /* Two finished states of the picker, worded apart because they ask
         different things of the reader. Everybody asked is a DONE state on a
         small installation — two accounts, both on the quote — and the select's
         generic "No matches" made the ordinary case read as a broken control.
         An empty roster is not the same fact: nobody has been asked, and
         somebody has to be invited before anybody can be. */
      allColleaguesAsked: 'Everyone who can be asked is already on this quote.',
      noColleagues: 'There is nobody else to ask — no other accounts exist yet.',
      askForPrice: 'Ask for a price',
      noteLabel: 'Note for them',
      /* A sentence, not a brief: the quote itself is where the detail lives, and
         a note long enough to hold a costing is one nobody reads in a bell. */
      noteHint: 'One line — what you need and by when. Optional.',
      /* Said after the write succeeded, because the point of the button is that
         somebody is told. Without this the press looks like it only added a row
         to a list. */
      notified: 'Asked — a notification has been sent.',
      /* A WARNING, never a gate. `sendable` and the checklist are untouched by
         this: a quote priced by the salesperson who wrote it is a normal quote,
         and refusing to send it would stop work the company does every day. */
      notReviewed: 'Nobody from Pricing has looked at this quote',
      notReviewedBody:
        'The offer can still be sent. Ask a colleague above if the number should be checked first.',

      /* The markup panel. Two percentages, two labels — see MarginPanel.

         NO `customerTotal` HERE, deliberately. The panel prints `totals.sell`,
         which is the same figure the cost table's footer prints, and since the
         panel was mounted on the costing tab the two sit centimetres apart on
         one screen. It words that figure with `quotes.costLines.customerTotal`
         — the costing's own vocabulary, shared with the phone list and the
         wizard header. A second entry here held the identical string and was
         one reword away from making one number look like two. */
      title: 'Price from the cost side',
      cost: 'Cost',
      /* Named for its denominator, because the field beside it is a percentage
         over the OTHER one and the pair only reads correctly when each says
         which it used. */
      marginOverRevenue: 'Margin over revenue',
      markup: 'Markup over cost, %',
      markupHint: 'The workbook’s figure: each line’s sell becomes its buy plus this much.',
      applyMarkup: 'Apply',
      applyMarkupConfirm:
        'Re-prices every line, including any priced by hand. A markup is a whole-quote figure.',
      /* The sharp edge of pricing from one column, said where the button is
         rather than in a comment. The markup is not stored — the workbook keeps
         it in a cell, this schema has no column for it — and a saved line
         writes the typed unit price to BOTH sides, so a line edited after the
         markup was applied comes back priced at cost while its neighbours keep
         their marked-up prices. Nothing warns about it afterwards, so it is
         said before. */
      markupNotStored:
        'The markup is not stored. Editing a line afterwards puts that line back to its cost while the others keep their prices, so apply the markup again when you have finished editing.',
      emptyCosting: 'Add a charge line before pricing the costing.',
      /* The OTHER refusal, and deliberately not worded as the one above. A
         markup is applied to a cost — sell becomes buy plus this much — so a
         costing whose lines all record a cost of zero has nothing for it to act
         on, and pressing it would set every price to zero. Says what the button
         does and why it cannot do it here; it does not send the reader to a
         field, because on this form there is none to send them to. */
      noCostSide:
        'Markup prices each line up from its cost, and every line here records a cost of zero — so there is nothing to price from.',
      preset: 'Preset',
      presetHint:
        'The standing charge lines, inserted with no amounts. Maintained in Settings.',
      noPreset: 'Settings holds no standing charge lines yet.',
    },
  },

  finance: {
    title: 'Money',
    /* "Who owes whom" is the actual question. Say "Money", never "Financials". */
    subtitle: 'Who owes whom.',
    sectionsLabel: 'Finance sections',

    tabs: {
      balances: 'Balances',
      aging: 'Aging',
      payments: 'Payments',
      approvals: 'Approvals',
      documents: 'Documents',
    },

    balances: {
      counterparty: 'Counterparty',
      type: 'Type',
      receivable: 'Receivable',
      payable: 'Payable',
      net: 'Net',
      oldest: 'Oldest',
      /* The age of the oldest unsettled item, in days. ALWAYS PRESENT and
         SIGNED: the live read sends a number on every row, and a party that has
         nothing outstanding is not on the list at all. A NEGATIVE age — nothing
         due yet — is rendered with the aging bucket's own words rather than a
         second phrasing here, so the two screens cannot come to disagree in one
         language and not the other. */
      oldestDays_one: '{{count}} day',
      oldestDays_other: '{{count}} days',
      /* Names the grid per issuing entity, because there is one table per entity
         (FR-35) and "Balances" four times over identifies none of them. */
      tableLabel: 'Balances — {{entity}}',
      emptyTitle: 'No balances in this period',
      emptyDescription:
        'A counterparty appears here once a case carries money against them — an invoice raised, a tranche due, or a vendor cost booked.',
    },

    aging: {
      title: 'Receivable by age',
      /* Five groups, not three. The two extra ones carry the whole point of
         the surface: FR-34 AC 5 forbids "outstanding" being read as "overdue",
         and AC 6 forbids an undated item being defaulted into a bucket. */
      buckets: {
        'not-yet-due': 'Not yet due',
        '0-30': '0–30 days',
        '31-60': '31–60 days',
        '60-plus': '60+ days',
        'no-date': 'No expected date',
      },
      emptyTitle: 'Nothing outstanding in this period',
      emptyDescription:
        'Tranches, issued invoices and vendor costs appear here until they are settled.',
    },

    payments: {
      title: 'Payments',
      record: 'Record payment',
      edit: 'Edit payment',
      reference: 'Reference',
      referenceHint: 'The bank reference, as it appears on the statement.',
      received: 'Received',
      amount: 'Amount',
      currency: 'Currency',
      direction: 'Direction',
      in: 'Incoming',
      out: 'Outgoing',
      /* Names the facet on the log. `direction` above labels the FIELD on the
         record sheet; a filter chip and a form label read differently even when
         they concern the same column. */
      directionLabel: 'Direction',
      allocated: 'Allocated',
      unallocated: 'Unallocated',
      /* The row opens as a sheet rather than expanding in place (UX-DR-4). */
      detailTitle: 'Payment {{reference}}',
      emptyTitle: 'No payments in this period',
      emptyDescription:
        'Money in and out appears here as it is recorded, with what each payment settles.',
      case: 'Case',
      caseHint: 'The deal this money moves against.',
      method: 'Method',
      methods: {
        cash: 'Cash',
        bank_transfer: 'Bank transfer',
      },
      /* OURS, not theirs — the two readings of "issuing entity" produce
         different books, so the label says whose. */
      entity: 'Our entity',
      entityHint:
        'Which of our legal entities the money reached or left — not the counterparty.',
      /* The booking the server took at the case's rate basis (AD-9). Read from
         the row, never recomputed on screen. */
      bookedAt: 'Booked at {{rate}} on {{date}} · {{usd}} USD',
      /* Unallocated cash is legal and visible; it is never auto-applied (AD-11). */
      allocationNote:
        'The payment lands unallocated. Apply it to invoices as a separate step.',

      /* The counterparty is PICKED from the directory rather than typed. Not
         validation: the name stays free text (0035). The point is spelling —
         a counterparty balance is keyed BY NAME, so "Zurdinov Ruslan" and
         "Zurdinov  Ruslan" are two half-right balances today. */
      counterpartyHint:
        'Who sent the money or whom we paid, as printed on the transfer. Picking from the directory is what keeps one name from becoming two balances.',
      counterpartyPlaceholder: 'Choose a counterparty',
      counterpartySearch: 'Search the directory',
      /* Clients and participants are two directories and one list here: money
         comes in from a client and goes out to a carrier. The hint on each row
         says which book it came from. */
      counterpartyKindClient: 'Client',
      /* A name saved before this field became a picker, or read off a transfer
         for a company nobody has a record of. It stays SELECTED: nothing here
         may reject data that is already saved. */
      counterpartyAsEntered: 'as entered',
      counterpartyAdd: 'Add a counterparty',

      /* The bank's charge for moving the money (migration 0058).
         BLANK means no fee was recorded, never a fee of zero — a zero would
         read on a report as a transfer that cost nothing. */
      feeHeading: 'Transfer fee',
      fee: 'Fee',
      feeCurrency: 'Fee currency',
      feeHint:
        'What the bank took to move this money. Optional. It never reaches the counterparty and settles nothing — only the payment amount above does.',
      /* On the payment card, its own line beside the amount. The two figures
         are never summed, which is why they are not in one total. */
      feeRecorded: 'Transfer fee {{amount}}',

      /* The pending-confirmation workflow (0059, spec L3/L4/L8).
       *
       * Every sentence here is about the RECORD and never about the money. A
       * pending payment is counted in «Оплачено», in the balances and in the
       * aging exactly like a confirmed one, so nothing may read as "this money
       * does not count yet" — the wording that would send somebody to pay a
       * carrier twice.
       *
       * And there is no «отклонён» status to name, because rejection is a
       * DELETION: a rejected row left on the ledger is a figure every sum then
       * has to remember to exclude. */
      status: 'Status',
      statuses: {
        pending: 'Awaiting confirmation',
        confirmed: 'Confirmed',
      },
      /* The badge on a row. Short, because it sits beside a reference and a
         figure — the long form is in the status column and the filter. */
      pendingTag: 'not confirmed',
      pendingFilter: 'Awaiting confirmation ({{count}})',
      confirm: 'Confirm',
      confirmedOn: 'Confirmed {{date}}',
      reject: 'Reject',
      rejectTitle: 'Reject payment {{reference}}',
      rejectBody:
        'The record is deleted together with what it settled. The carrier is still owed the cost — that line stays. Only a payment nobody has confirmed can be rejected.',
      rejectReason: 'Reason',
      rejectReasonHint:
        'Required. The row is about to be gone, so the audit trail is the only place left that can say why.',
      rejectSubmit: 'Reject and delete',
      amendTitle: 'Correct payment {{reference}}',
      amendSubmit: 'Save the correction',
      amendFixedFacts:
        'The currency ({{currency}}) and the date the money moved ({{date}}) cannot be corrected — every booking on this payment hangs off them. If either is the mistake, reject the record with a reason and enter it again.',
      amendAction: 'Correct payment {{reference}}',
      rejectAction: 'Reject payment {{reference}}',
    },

    /* Allocation — saying what a payment settles (Story 4.5, AD-11).
     *
     * The copy no longer apologises for a missing picker. A tranche source is
     * registered, so the case's own tranches are what a split settles; invoices
     * and debit notes stay unavailable until 5.5 and 5.6, and `targetsTrancheOnly`
     * is where that is said once rather than in three field hints. */
    allocations: {
      heading: 'Allocated to',
      allocate: 'Allocate',
      submit: 'Allocate',
      title: 'Allocate {{reference}}',
      none: 'Nothing allocated yet. Unallocated cash is legal — it is applied when someone says what it settles, never automatically.',
      fullyAllocated: 'This payment is fully allocated — there is nothing left to apply.',
      target: 'Settles',
      targetPlaceholder: 'Choose a tranche',
      targetN: 'Split {{n}} target',
      targetRequired: 'Choose what this split settles.',
      /* The sequence is the tranche's meaning, not its position in the list. */
      trancheLabel: 'Tranche {{sequence}} of {{total}}',
      trancheHint: '{{amount}} · {{when}}',
      /* A tranche nobody has dated yet. Not chaseable, and not a blank. */
      trancheNoDate: 'no date agreed',
      targetsLoading: 'Reading this case’s expected tranches…',
      targetsNone:
        'This case has no expected tranches, so there is nothing for this payment to settle. Tranches are created when the case’s rate is agreed — record the rate first, then allocate.',
      targetsTrancheOnly:
        'A payment settles the case’s expected tranches. Invoices and debit notes cannot be settled yet.',
      /* An OUTBOUND payment settles a cost, never a tranche — the server
         refuses the mismatch outright (`ErrAllocationDirectionMismatch`), so
         offering tranches here was offering the one thing that cannot work.
         The empty state says the act that would change it rather than repeating
         the receivable advice, which has nothing to do with paying a carrier. */
      expenseTargetsNote:
        'The costs recorded on this case — an outgoing payment settles those, not the client’s tranches.',
      expenseTargetsNone:
        'No costs are recorded on this case yet, so there is nothing for this payment to settle. Record what is owed first — «Оплатить перевозчику» on the direction does both in one step.',
      /* Said on the BUTTON rather than inside the sheet. Opening a picker whose
         read has not answered would show "this case has no costs" about a case
         that may well have several. */
      expenseTargetsLoading: 'Reading the costs recorded on this case…',
      expenseTargetsFailed:
        'The costs on this case could not be read, so there is nothing safe to offer. Retry before allocating.',
      targetsFailed:
        'This case’s tranches could not be read, so the list may be incomplete. Retry before allocating.',
      amountN: 'Split {{n}} amount',
      amountHint: 'In {{currency}}, the currency the payment arrived in.',
      add: 'Add split',
      remove: 'Remove split {{n}}',
      requested: 'Applying',
      splitsRequired: 'Name the document and the amount for every split.',
      exceedsBalance:
        'The splits add up to more than the {{balance}} still unallocated on this payment.',
      /* The settlement booking on the allocation row — not the document's own,
         which is never rewritten (AD-9). */
      settledAt: 'at {{rate}} · {{usd}} USD',
      /* The settlement list's columns. «Booked» names the cell that reads
         «at 1 · 2 500.00 USD» — the rate this settlement was taken at and
         what it came to in USD, which is the target's booking and not the
         payment's. */
      columns: { target: 'Settles', amount: 'Applied', booked: 'Booked at', at: 'Allocated' },
      fxDifference: 'FX {{value}}',
      fxDifferenceHint:
        'The difference between settling at this rate and the rate the document was booked at. Recorded here; the document’s booked value is unchanged.',
    },

    approvals: {
      pending: 'Pending',
      current: 'Current',
      requested: 'Requested',
      change: 'Change',
      approve: 'Approve',
      reject: 'Reject',
      confirmTitle: 'Approve the rate change on {{number}}',
      confirmBody:
        'The rate on {{number}} changes from {{current}} to {{requested}}. Approval is binding — a wrong rate needs a new request, not an undo.',
      emptyTitle: 'No approvals waiting',
      emptyDescription:
        'Rate changes needing a decision appear here and stay until resolved.',
    },

    documents: {
      title: 'Invoices and debit notes',
      newInvoice: 'New invoice',
      edit: 'Edit document',
      number: 'Number',
      /* The number is minted from the entity's scheme, never typed (AD-12). */
      numberNote: 'The number is assigned from this entity’s numbering scheme.',
      entity: 'Issuing entity',
      case: 'Case',
      issued: 'Issued',
      status: 'Status',
      due: 'Due',
      total: 'Total',
      outstanding: 'Outstanding',

      /* Everything from here down is the raise → generate → issue sheet and
       * its preview, and it lives under `finance` rather than under `cases`
       * on purpose: the case's Documents section and the Finance page raise
       * and preview through the SAME component and the same mutation. The
       * case adds a filter and a prefill; it does not fork the form. A second
       * copy of this copy under `cases` would be the first step towards
       * forking it, and the two screens would then drift apart in wording
       * before they drifted apart in behaviour.
       */

      /* The two lists the section is split into, and the render state of a row
         inside them. "Not rendered" is SAID rather than left blank, because a
         blank cell in an archive reads as a column that failed to load rather
         than as a document nobody has generated yet. */
      debitNotes: 'Debit notes',
      invoices: 'Invoices',
      rendered: 'Rendered',
      notRendered: 'Not rendered',

      /* Three verbs, three server calls, and deliberately no shortcut that
         does two of them at once: generating is not issuing (UX-DR-15), and an
         "issue on generate" flag is something a client sets by accident.
         "Generate again" is a separate word from "Generate" because the second
         press replaces a file that is already in the archive, and a button
         that still said "Generate PDF" would read as a first render. */
      actions: {
        generate: 'Generate PDF',
        regenerate: 'Generate again',
        generating: 'Generating…',
        issue: 'Issue',
        issuing: 'Issuing…',
        /* The row's own actions. Icons, but every one of them names itself —
           read aloud by a screen reader, shown on hover — and the two
           dangerous ones (issue, discard) are decided in a dialogue where
           everything is spelled out in words. An icon here opens a
           conversation; it does not commit an act. */
        preview: 'View the document',
        open: 'Open the invoice',
        discard: 'Discard the draft',
      },

      /* Only a draft can be discarded. A client is holding the issued one, and
         a number that leads nowhere is worse than a document somebody has to
         explain (AD-24). The ceiling is named separately because it is the
         reason to discard at all: an abandoned draft's charges eat the headroom
         of every invoice raised after it. */
      discardConfirm: {
        title: 'Discard this draft?',
        body: 'The draft and its charges are removed for good. While it exists, its figures take up part of the agreed rate and reduce what is left for later invoices.',
        confirm: 'Discard',
      },

      /* Issue is the confirmed one because it is the irreversible one.
         Generating can be repeated all afternoon and costs nothing but a
         render; issuing mints the number, freezes the document and creates the
         receivable, and AD-24 makes that one-way. The body names all three
         consequences and then names the remedy, because "cannot be undone"
         on its own reads as a dead end rather than as the ordinary way paper
         is corrected. */
      issueConfirm: {
        title: 'Issue this document?',
        body: 'Issuing mints the number from this entity’s scheme, freezes the document and creates the receivable. It cannot be undone — a correction is a new document, raised against this case.',
        confirm: 'Issue',
      },

      /* AC 7, and a blocking message in the house form: the block and its
         reason together, so the rule is learned once. The number is stamped
         onto the archived file, so a record nobody has rendered has nothing to
         stamp — which is the whole of the reason, and why this says what the
         PDF is for instead of saying "not allowed yet". */
      needsRender: 'The PDF has to be generated before this can be issued — the number is stamped onto the archived file.',
      /* AC 9. This is the affordance, not the guard: the server refuses the
         edit regardless. The sentence exists so the screen says why the fields
         are gone rather than presenting ones that would be rejected. */
      issuedReadOnly: 'An issued document cannot be edited. Raise a new one to correct it.',
      /* The refusal a SECOND Issue press earns. Worded as a fact about the
         first press rather than as an error, because what the caller needs to
         learn is that the document went out — swallowing the refusal, or
         dressing it as a failure, teaches the opposite (UX-DR-24). */
      alreadyIssued: 'This document has already been issued.',
      /* The `document` module is nil-able: with no object storage configured
         main.go never constructs it and its routes are never mounted, so a
         laptop without a bucket gets a 404. It must not render as an empty
         list — an empty list says there is no paper on this case, and the
         truth is that there is no archive to look in. */
      archiveUnavailable: 'The document archive is not configured in this environment, so nothing can be rendered or shown here.',

      /* Two different things wear the word "preview", and this copy is what
         keeps them apart. The DRAFT preview is a layout the client draws from
         the fields on screen — no render, no archive row — so that the
         type-a-line-see-it loop does not cost a page render per keystroke. The
         RENDERED one is the archived PDF itself, the bytes the client
         receives. Somebody who only ever saw the first has not seen the
         document, which is why the second exists and why neither may borrow
         the other's words or offer the other's actions. */
      preview: {
        /* The sheet that shows the SERVER-composed page — the same template
           the client's PDF comes off. It sits in the same branch as the words
           below on purpose: those describe the hand-drawn layout this replaces,
           and keeping both under one key is what makes the pair visible to
           whoever removes the last of the old one. */
        title: 'Document',
        subtitle: 'How the document reads on paper. A draft prints DRAFT where its number will go.',
        composeFailed: 'The page could not be composed.',

        draftBadge: 'Draft',
        draftTitle: 'Draft preview',
        draftHint: 'This is a layout of what has been typed, not the document. Press Generate to produce the PDF the client will receive.',
        renderedTitle: 'Rendered document',
        /* AC 8. Shown instead of yesterday's PDF beside today's figures: the
           screen falls back to the live draft and says which of the two the
           reader is now looking at, rather than silently keeping a stale
           frame that still looks authoritative. */
        outOfDate: 'This draft has changed since it was rendered, so the archived copy is out of date. Generate again before issuing.',
        /* The signed URL is good for five minutes (AD-18), so that a link
           copied out of the page dies before it can be shared. Naming the
           expiry is what makes the retry beside this read as the ordinary next
           step rather than as a fault the reader caused. */
        failed: 'The document could not be loaded. Its link expires after five minutes.',
        loading: 'Loading the document…',
        empty: 'Pick a document on the left to see it.',
        /* The <iframe>'s accessible name. Without it a screen reader announces
           an unnamed frame in the middle of the page and the reader has no way
           to know what it holds. */
        frameTitle: 'Document preview',
      },

      /* The raise form itself — one form, both screens. */
      raise: {
        kind: 'Kind',
        counterparty: 'Billed to',
        /* Not cosmetic, which is why it is said out loud: the printed name and
           address on the paper are resolved from the party picked here
           (AD-26), so this control decides what the client reads at the top of
           the document, not just which row the debit note is filed under. */
        counterpartyHint: 'A debit note is addressed to the client or to one of this case’s participants. The printed name and address are resolved from the party picked here (AD-26).',
        reference: 'Their reference',
        noteDate: 'Document date',
        currency: 'Currency',
        /* An invoice arrives with the accepted quote's charges, copied by the
           server. Saying so is what stops somebody hunting this form for a
           charge table that is deliberately not on it. */
        submit: 'Raise',
      },

      /* The fork under "New document". The two papers are not two flavours of
       * one form: an invoice bills the CLIENT out of the rate agreed with them
       * (AD-10), a debit note re-bills a cost to whoever owes it (AD-26). The
       * question used not to be asked at all — the first screen offered the
       * case's own costs as tickboxes, and any tick silently made the document a
       * debit note. Hence a sentence per item: two words do not say which side
       * of the deal the paper faces or where its lines come from. */
      new: {
        invoice: 'Client invoice',
        invoiceHint: 'From the agreed rate. The charges are prefilled from the quote.',
        debitNote: 'Debit note',
        debitNoteHint: 'Re-bill a case cost to a counterparty.',
      },

      lines: {
        label: 'Charges',
        /* A debit note IS its lines: the client is being billed for something
           nobody quoted, so "which charge" is the whole question. */
        empty: 'No charges yet. A debit note needs at least one — the client is being billed for something that was never quoted.',
        add: 'Add charge',
        description: 'What is being charged',
        descriptionN: 'Charge {{n}} description',
        notePlaceholder: 'Qualifier, e.g. 4 Containers (Open-top) — optional',
        noteN: 'Charge {{n}} qualifier',
        unitPrice: 'Unit price',
        unitPriceN: 'Charge {{n}} unit price',
        quantity: 'Qty',
        quantityN: 'Charge {{n}} quantity',
        remove: 'Remove charge {{n}}',
      },
    },

    /* The invoice's own workplace — `routes/CaseInvoicePage.tsx`.
     *
     * A PAGE rather than the third modal in a chain, because an invoice is a
     * document that prints on A4 and is read across: the charges, the ceiling
     * they are judged against and the booking that converts them are one
     * picture, and a 440px column can hold at most one of the three at a time.
     *
     * The copy lives under `finance` beside `documents` and `invoices` rather
     * than under `cases`, for the reason the block above states: the case's
     * section and the Money page reach the same paper, and a second catalogue
     * for one of them is where two screens start describing one record
     * differently. */
    invoiceDesk: {
      /* The way back. It names the CASE and the section, because the reader
         arrived from one of two places and both of them are that section. */
      back: 'Invoices',
      title: 'Client invoice',
      trancheN: 'Tranche {{n}}',
      /* An invoice that bills no instalment. Not "none" and not a blank: a
         re-billing, a correction and a case with no payment series are all
         ordinary, and the phrase has to read as a deliberate answer rather than
         as a field somebody failed to fill. */
      offSchedule: 'Off schedule',
      /* The option's own label while the tranche list is still in flight, or
         after that read failed — the invoice carries the link either way, and a
         select that fell back to "Off schedule" would state the opposite of
         what the record says. */
      trancheLinked: 'Scheduled payment',
      trancheOption: 'Tranche {{n}} · {{amount}}',
      numberOnIssue: 'the number is assigned when it is issued',
      issuedOn: 'Issued {{date}}',
      closedTranche: 'closed tranche {{n}}',

      whatWeBill: 'What is being billed',
      tranche: 'Scheduled payment',
      /* Said before the press, not after it. Issuing against a tranche removes
         that tranche from the allocation registry — the invoice takes its place
         — and a reader who does not know that reads the disappearance as data
         going missing. */
      trancheClosesNote:
        'Issuing closes tranche {{n}}: it stops being counted as an expected amount of its own, so the receivable is not claimed twice.',
      offScheduleNote:
        'No tranche is closed: the payment schedule stays as it is and this invoice becomes a receivable in its own right. The approved rate still caps it.',
      /* AD-9. The currency is one of the five facts frozen together at
         creation, so it is shown and not offered. */
      currencyFrozen: 'Frozen with the rate. A different currency is a new invoice.',

      lines: 'Invoice lines',
      linesFromQuote:
        'Copied from the accepted quote {{quote}}. Corrected here, one charge at a time.',
      linesOwn:
        'This case was opened without a quote, so there was nothing to copy — the charges are typed here.',
      fromQuote: 'from the quote',

      /* The tranche a demand bills and the charges on it are two figures that
         are meant to agree, and nothing on the server makes them. The banner
         states the gap rather than closing it: reconciling the two is a
         judgement — split the charges, move the invoice off the schedule, or
         re-cut the tranche — and none of those is a button this screen can press
         on somebody's behalf. */
      mismatchTitle: 'The charges do not add up to tranche {{n}}',
      mismatchBody:
        'The charges come to {{lines}} and tranche {{n}} expects {{tranche}}. Issued as it stands, this invoice closes that instalment with a document for a different amount, and the rest of the schedule is left uncovered. Correct the charges, or move this invoice off the schedule.',

      ceiling: 'Approved rate on this case',
      ceilingApproved: 'Agreed with the client',
      ceilingEarlier: 'Billed earlier',
      ceilingThis: 'On this invoice',
      ceilingRemaining: 'Left for the next invoice',
      /* The case has no agreed rate, which means there is NO ceiling — not a
         ceiling of zero. An invoice can still be raised; it simply has nothing
         to be checked against. */
      noCeilingTitle: 'No approved rate',
      noCeilingBody:
        'This case was opened directly, without a quote, so there are no charges to copy and no ceiling on the invoice. It can still be raised — it just has nothing to be checked against.',

      booking: 'How this converts to USD',
      bookingCurrency: 'Document currency',
      bookingRate: 'Rate for {{date}}',
      bookingNote:
        'The rate is fixed when the invoice is created and is never recalculated. A different currency is a new invoice.',

      prints: 'What will print',

      next: 'What happens next',
      nextOne: 'A draft can be corrected as often as you like.',
      nextTwo:
        'The PDF is built and filed in the case archive. Any edit to the draft cancels it.',
      nextThree:
        'Issuing assigns the number, closes the tranche and creates the receivable. It cannot be undone.',

      faceValue: 'Invoice total',
      received: 'Received',
      outstanding: 'Still owed',
    },

    /* Client invoices raised against a case (Story 5.4, FR-61).
     *
     * "Invoice" here is the paper sent to the client, not the archive row
     * behind it — the two are separate records and the vocabulary has to stay
     * separate with them, or "issue" and "render" become the same word. */
    invoices: {
      title: 'Client invoices',
      new: 'New invoice',
      newTitle: 'New invoice on {{number}}',
      raise: 'Raise draft',
      /* The title while the invoice is still loading, or after it failed. The
         one above interpolates the case number, and rendering it from a record
         that has not arrived produces the dangling "Draft invoice on ". */

      caseLabel: 'Case',
      casePlaceholder: 'Choose a case',
      /* Named as a business rule rather than left as a greyed-out button: an
         invoice belongs to a case, and this says which step comes first. */
      chooseCaseFirst: 'Choose a case first — an invoice is always raised against one.',
      noCaseTitle: 'Choose a case',
      noCaseDescription:
        'Invoices are raised against a case, so its client, its quote and its approved rate come with them. Pick one above.',
      casesUnavailable: 'The case list could not be loaded, so there is nothing to choose from yet.',
      emptyTitle: 'No invoices on this case',
      emptyDescription:
        'Raise one to bill the client. A case may carry several — one per payment tranche.',
      /* A draft has no number. Story 5.5 mints it from the entity's scheme when
         the invoice is issued, and anything printed here in the meantime would
         belong to no sequence (AD-12). */
      unnumbered: 'No number yet',
      /* Per ROW, not per case. Every invoice on a case-scoped list carries the
         same case number, so interpolating that gave a screen reader half a
         dozen controls with one identical name. The invoice's own number
         identifies it once it has one; until then its date does. */
      open: 'Open the invoice {{invoice}} dated {{date}}',

      invoiceDate: 'Invoice date',
      economicDate: 'Economic date',
      economicDateHint: 'The date the FX rate is taken for.',
      /* Said rather than hidden: the booking is five facts frozen together
         (AD-9), and a field that quietly vanished on save would look like a
         bug instead of a rule. */
      economicDateFrozen: 'Frozen with the rate. Raise a new invoice to change it.',
      customerReference: 'Your ref.',
      customerReferenceHint: "The client's own reference, as it prints on the invoice.",

      currency: 'Document currency',
      currencyHint:
        'Chosen once. It books the rate and the USD equivalent with it, and neither moves again.',

      vat: 'VAT',
      vatApplies: 'VAT applies',
      /* FR-10: the block is printed or omitted, never printed as zero — a zero
         VAT line is a statement about a zero-rated supply. */
      vatHint: 'The tax block prints only when this is ticked.',
      vatRate: 'VAT rate, %',
      vatRateHint: 'A percentage — 20 means 20%.',
      vatAmount: 'Tax on this invoice: {{amount}}',

      paymentTerms: 'Payment terms',
      bankDetails: 'Bank details',
      /* FR-64/AC-5: never typed on the invoice. Saying where they come from is
         what stops somebody looking for the field to edit. */
      termsNote:
        'Payment terms and bank details resolve from the issuing entity and are not editable here. Change them in Settings and every invoice follows.',
      saveHeader: 'Save header',

      lines: {
        label: 'Charges',
        empty:
          'No charges yet. A case converted from a quote arrives with the lines the client accepted; a case raised directly starts blank.',
        description: 'What is being charged',
        quantity: 'Qty',
        unitPrice: 'Unit price',
        amount: 'Amount',
        amountHint: 'What is billed, in {{currency}}.',
        /* Zero is refused with the negatives, as the server refuses it: a
           charge at 0.00 reads on a client's invoice as a fee waived rather
           than as a figure nobody typed. */
        amountPositive: 'Enter an amount greater than zero',
        /* Shown only when the amount does NOT equal the product of the columns
           beside it, which means somebody typed over the calculation. That is
           legitimate — a lump sum, a round figure, something agreed on the
           phone — but worth saying out loud, because a half-finished edit looks
           exactly the same. In the ordinary case the product is already in the
           field itself, and a hint that restates its own field teaches people
           to stop reading hints. */
        amountPreview: 'Does not match qty × unit price = {{amount}}',
        add: 'Add charge',
        saveCharge: 'Save charge',
        editN: 'Edit charge {{n}}',
        remove: 'Remove charge {{n}}',
        /* AD-10, refused at the point of ADDING the line rather than at issue.
           The figures are composed here from the invoice already on screen —
           the server deliberately sends none inside the refusal. */
        exceedsApprovedRate:
          'This charge takes the billed total {{excess}} past the approved rate of {{approved}}. {{billed}} is billed on this case already. An approved rate change is needed before it can be added.',
        exceedsShort: 'Past the approved rate',
        exceedsUnknownRate:
          'This charge would bill the client past the approved rate on this case. An approved rate change is needed before it can be added.',
      },
    },
  },

  clients: {
    title: 'Clients',
    subtitle: 'Who owes what, and every shipment behind the figure.',
    new: 'New client',
    year: { label: 'Statement year', all: 'All years' },
    tiles: {
      billed: 'Billed',
      received: 'Received',
      balance: 'Balance',
      balanceToday: 'Balance today',
      opening: 'Opening balance',
      cost: 'Cost',
    },
    filters: { withBalance: 'With balance only' },
    columns: {
      client: 'Client',
      managers: 'Manager',
      cases: 'Cases',
      weight: 'Weight, kg',
      billed: 'Billed',
      received: 'Received',
      balance: 'Balance',
      cost: 'Cost',
      status: 'Status',
    },
    table: { label: 'Clients', emptyTitle: 'No clients yet', emptyDescription: 'Add the first client, and every quote starts by picking one.' },
    actions: { edit: 'Edit', merge: 'Merge', open: 'Open client' },
    moneyUnavailable: {
      title: 'Figures temporarily unavailable',
      body: 'The directory is complete; the money could not be read right now. Try again in a minute.',
    },
    statement: {
      title: 'Statement',
      eyebrow: 'Client',
      tableLabel: 'Statement of {{name}}',
      openingRow: 'Opening balance',
      totalRow: 'Total',
      emptyTitle: 'No shipments this year',
      emptyDescription: 'Pick another year, or all years.',
      details: 'Details and contacts',
      columns: {
        no: 'No',
        openedOn: 'Receipt date',
        number: 'Case',
        route: 'Route',
        mode: 'Transport',
        cargo: 'Goods',
        weight: 'Weight, kg',
        volume: 'Volume, m³',
        billed: 'Billed',
        received: 'Received',
        running: 'Balance',
        paymentMethod: 'Payment',
        lastReceivedOn: 'Collected on',
        ata: 'Arrived',
        transitDays: 'Days',
        owner: 'Manager',
        cost: 'Cost',
        status: 'Status',
      },
    },
  },

  directories: {
    title: 'Directories',
    subtitle: 'The records every deal joins to, kept in one place.',
    sectionsLabel: 'Directory sections',

    tabs: {
      clients: 'Clients',
      participants: 'Participants',
      users: 'Users',
      transport: 'Transport',
    },

    /* A refused delete (FR-39, Story 7.1 AC 4, Story 7.4 AC 5).
     *
     * The record numbers are interpolated rather than concatenated at the call
     * site, because the list sits in a different place in the Russian clause and
     * a sentence assembled from fragments cannot move it (UX-DR-30). */
    referenced: {
      /* `more`, never `count`: i18next reads `count` as the pluralisation
         trigger and would then look for andMore_one / andMore_other here and
         four Russian forms next door, none of which this phrasing needs — "and
         3 more" agrees with nothing. A plural key nobody wrote is a key that
         falls back to the raw token in front of a user. */
      andMore: '{{named}} and {{more}} more',
      mergeInstead:
        'This record is used by {{list}}, so deleting it would orphan them. Merge it into another record instead.',
      deactivateInstead:
        'This entry is used by {{list}}, so it cannot be deleted. Deactivate it instead — it will stop being offered on new records and stay on the ones that already use it.',
      // The merge that cannot run because this deployment has no way to move a
      // reference. A different sentence from the two above on purpose: it must
      // not send the reader back to the action that just refused them.
      mergeUnavailable:
        'This record is used by {{list}} and this installation cannot move those references yet, so merging would orphan them. Deactivate it instead.',
    },

    /* Merging one record into another — the action FR-39 offers when a delete is
     * refused. */
    merge: {
      action: 'Merge',
      title: 'Merge into another record',
      description:
        'Everything that names this record will name the one you choose, and this record is then removed. It cannot be undone.',
      target: 'Merge into',
      targetPlaceholder: 'Choose the record to keep',
      confirm: 'Merge and remove',
      sameRecord: 'Choose a different record to merge into.',
    },

    activate: 'Activate',
    deactivate: 'Deactivate',
    // Why the control is refusing, shown on hover rather than by disabling it:
    // a disabled button makes its own title unhoverable (UX-DR-22).
    needsGrant: 'You need the directory grant for this list.',

    /* The contacts block, shared by clients and participants (FR-39, FR-41). */
    contacts: {
      legend: 'Contacts',
      label: 'Who this is',
      labelPlaceholder: 'e.g. Dispatch desk',
      add: 'Add a contact',
      remove: 'Remove this contact',
      primary: 'Primary contact',
      handlePlaceholder: 'handle or profile link',
      /* Said plainly, because a field that looks like a delivery channel will
         be treated as one. */
      messengerNote:
        'Contact details only. The platform does not send messages to these — messaging stays outside it.',
      restricted: 'Contact details are not available to your role.',
    },

    clients: {
      title: 'Clients',
      new: 'New client',
      delete: 'Delete',
      edit: 'Edit client',
      name: 'Name',
      country: 'Country',
      taxId: 'Tax ID',
      registrationId: 'Registration ID',
      address: 'Address',
      code: 'Client code',
      /* It expands into a document number (FR-39, FR-68), so somebody
         choosing one should know where it shows up. */
      codeHint: 'Optional. Can be used in document numbers, so keep it short.',
      codeInvalid: 'Letters, digits and hyphens only',
      contact: 'Contact',
      owner: 'Owner',
      open: 'Open',
      receivable: 'Receivable',
    },

    participants: {
      title: 'Participants',
      new: 'New participant',
      edit: 'Edit participant',
      type: 'Type',
      wechat: 'WeChat',
      name: 'Name',
      country: 'Country',
      owner: 'Owner',
      contact: 'Contact',
    },

    users: {
      title: 'Users',
      new: 'New user',
      edit: 'Edit user',
      email: 'Email',
      invalidEmail: 'Enter a valid email address',
      role: 'Role',
      office: 'Office',
      grants: 'Grants',
      /* Grants are additive booleans on the user, never new roles (AD-7). */
      grantLabels: {
        manageClients: 'Manage clients',
        manageParticipants: 'Manage participants',
        manageTransport: 'Manage transport references',
      },
      status: 'Status',
      inactive: 'Inactive',
      language: 'Interface language',
      /* An address identifies an account; changing one would rewrite who every
         historical row was attributed to (Story 1.3). */
      emailFixed: 'The email address cannot be changed after the account is created.',
      awaitingPasswordChange: 'Password not yet changed',
      resetPassword: 'Reset password',
      deactivate: 'Deactivate',
      activate: 'Reactivate',
      /* The credential hand-off. The password is mailed to its owner, and is
         also shown here exactly once — the server stores only its hash. Both
         halves are in the copy because the screen is the fallback: it is what
         still works when the address is wrong or mail is down. */
      credentialTitle: 'Password for {{name}}',
      credentialIntro:
        'The account is ready to use. The password has been emailed to {{email}} with a link to sign in, and is shown here in case you need to hand it over yourself.',
      credentialCopy: 'Copy',
      credentialCopied: 'Copied',
      credentialWarning:
        'This password is shown once here and cannot be recovered. Its owner is asked to replace it when they first sign in. If the email does not arrive, reset the password to issue and send a new one.',
      credentialDone: 'I have saved it',
    },

    transport: {
      title: 'Transport',
      modes: 'Modes',
      equipmentTypes: 'Equipment types',
      add: 'Add',
      mode: 'mode',
      equipment: 'equipment type',
      addItem: 'New {{item}}',
      editItem: 'Edit {{item}}',
      code: 'Code',
      codeHint: 'Short machine-facing code, e.g. FCL or 40HC.',
      delete: 'Delete',
    },

    /** The row action that opens the edit sheet, named for screen readers. */
    editRecord: 'Edit {{name}}',
  },

  settings: {
    title: 'Settings',
    subtitle: 'How documents are numbered, branded and worded.',
    sectionsLabel: 'Settings sections',

    tabs: {
      entities: 'Issuing entities',
      numbering: 'Numbering',
      templates: 'Templates',
      directions: 'Directions',
      access: 'Roles & permissions',
    },

    /* Roles & permissions — the editable model of migration 0021.
     *
     * Wording note: a "capability" is what somebody may DO and "access" is
     * whose records they may SEE. The two are named differently throughout
     * because they are different axes, and a founder who conflates them builds
     * a role that can issue invoices against no cases. */
    roles: {
      title: 'Roles',
      intro:
        'A role is a set of permissions you name. People hold one role, plus any extra permissions granted to them individually.',
      create: 'New role',
      loading: 'Loading roles',
      edit: 'Edit {{name}}',
      editAction: 'Edit {{name}}',
      deleteAction: 'Delete {{name}}',
      loadFailed: 'The roles could not be loaded. Try again.',
      deleteFailed:
        'That role could not be deleted. Roles that people still hold must be reassigned first.',
      superuserLocked:
        'This role always has every permission, so there is nothing to tick. That is what makes it the role that can restore the others.',
      badges: {
        superuser: 'Full access',
        system: 'Built in',
      },
      summary: {
        allPermissions: 'Every permission',
        counts: '{{capabilities}} permissions · {{surfaces}} areas',
        users_one: '{{count}} person',
        users_other: '{{count}} people',
      },
      fields: {
        key: 'Identifier',
        keyHint: 'Lowercase letters, digits and hyphens. Cannot be changed later.',
        name: 'Name',
        description: 'Description',
        descriptionHint: 'What this role is for, in your own words.',
      },
      scopes: {
        title: 'What they can see',
        hint: 'Whose records this role reaches. Set an area to no access and it disappears from their navigation entirely.',
      },
      capabilities: {
        title: 'What they can do',
        hint: 'Every permission is off until you turn it on.',
      },
      levels: {
        none: 'No access',
        own: 'Only their own',
        assigned: 'Their own and ones they are on',
        all: 'Everything',
      },
      surfaces: {
        quotes: 'Quotes',
        cases: 'Cases',
        'case-money': 'Case costs and profit',
        'cross-case-finance': 'Company finance',
        clients: 'Clients',
        participants: 'Participants',
      },
      groups: {
        quote: 'Quotes',
        case: 'Cases',
        directory: 'Directories',
        finance: 'Finance',
        document: 'Documents',
        collab: 'Files and chat',
        admin: 'Administration',
      },
    },
    access: {
      title: 'Access matrix',
      subtitle:
        'What each role may see and do. Roles are fixed; a person is given one on their user record.',
      tableCaption: 'Access by data group and role',
      dataGroup: 'Data group',

      levels: {
        full: 'All',
        scoped: 'Assigned',
        read: 'Read',
        hidden: 'Hidden',
        none: 'No access',
      },

      detail: {
        ownOnly: 'Own only',
        ownDeals: 'Own deals',
        ownQuotes: 'Own quotes',
        assigned: 'Where assigned',
        readCreateOwn: 'Read and create own',
        salesDirectorGrant: 'Sales director via grant',
        pricingDirectorGrant: 'Pricing director via grant',
        maintainAgents: 'Maintains agents',
        participantsSales:
          'Agents hidden; shipper, consignee and driver visible on own cases',
      },

      groups: {
        operational: 'Quotes and cases — operational data',
        commercial: 'Deal commercial data — cost lines, markup, rate, expenses',
        agentIdentity: 'Agent identity in structured fields',
        crossCaseFinance: 'Cross-case finance — aging, balances, all-case profit',
        pipeline: 'Quote pipeline and win/loss stats',
        clients: 'Client directory',
        participants: 'Agent and participant directory',
      },

      enforcementNote:
        'Enforced on the server for every request. What the interface hides is a convenience, never the boundary itself.',

      grantsTitle: 'Ownership grants',
      grantsSubtitle:
        'Additive rights on top of a role, not roles of their own. A grant widens a directory; it never widens access to cases or money.',
      grants: {
        manageClients:
          'Manage the whole client list, including records owned by other people — merging and deleting included.',
        manageParticipants:
          'Manage the whole participant list, agents included — merging and deleting included.',
        manageTransport: 'Maintain transport modes and equipment types.',
      },
      grantsNote:
        'Granted per person in Directories → Users. Founders hold all three.',

      boundariesTitle: 'Boundaries worth stating',
      boundaries: {
        ownDealMoney: {
          title: 'A deal team sees its own deal’s money',
          body:
            'Sales records the rate and expected costs, so they see them. The founder-only restriction is on cross-case finance — the dashboard, aging and counterparty balances — not on one’s own deal.',
        },
        agentIdentityScope: {
          title: 'Agent hiding covers structured fields only',
          body:
            'Directory records, participant slots and vendor tags are suppressed for Sales, and read “Agent hidden” rather than sitting empty. Uploaded files and case chat are not redacted.',
        },
      },
      externalTag: 'Not users',
      externalNote:
        'Clients, shippers, consignees, agents, vehicle owners, dispatchers and drivers never log in. They receive email only.',
    },

    entities: {
      title: 'Issuing entities',
      new: 'New entity',
      edit: 'Edit entity',
      name: 'Entity name',
      legalFooter: 'Legal footer',
      /* The two short codes a numbering pattern expands (FR-68). */
      deactivate: 'Deactivate',
      activate: 'Reactivate',
      inactive: 'Deactivated',
      /* An unset footer or bank block reaches a client's paper as a blank. */
      notSet: 'Not set',
      initials: 'Initials',
      officeCode: 'Office code',
      defaultOffice: 'Default for office',
      defaultOfficeHint:
        "Records created by this office's users are issued from this entity. At most one entity per office.",
      noDefaultOffice: 'No office defaults to this entity',
      legalFooterHint: 'Printed at the foot of every document this entity issues.',
      bankDetails: 'Bank details',
      emailSignature: 'Email signature',

      /* The uploaded mark (FR-64, migration 0041). Until this existed, every
         document printed the entity's NAME where its logo belongs. */
      logo: {
        upload: 'Upload logo',
        replace: 'Replace logo',
        remove: 'Remove',
        sending: 'Sending the logo…',
        /* Says what a document will do, not what the form accepts: the reader's
           question is what a client receives. */
        hint: 'PNG or JPEG, up to 2 MB. Printed on every offer, invoice and debit note this entity issues. Without one, documents carry the entity name instead.',
        errorType: 'That file type cannot be printed on a document. Use PNG or JPEG.',
        errorSize: 'That logo is larger than 2 MB. Export it smaller and try again.',
        errorUpload: 'The logo could not be sent to storage. Check the connection and try again.',
        errorGeneric: 'The logo could not be saved.',
      },
    },

    numbering: {
      title: 'Numbering schemes',
      new: 'New scheme',
      edit: 'Edit scheme',
      entity: 'Entity',
      document: 'Document',
      pattern: 'Pattern',
      kinds: {
        quote: 'Quote',
        case: 'Case',
        invoice: 'Invoice',
        'debit-note': 'Debit note',
      },
      /* Shown when no pattern is stored and the onboarding default applies. */
      usingDefault: 'Default',
      /* The server refuses a pattern with a stable token; these are its
         translations (AD-27). */
      errors: {
        missingSeq: 'A pattern needs {seq}, or every record gets the same number.',
        unknownToken:
          'Unknown token. Use {INITIALS}, {OFFICE}, {YY}, {YYYY}, {MM} or {seq}.',
        seqTooWide: 'The sequence padding is too wide.',
        invalid: 'This pattern cannot be used.',
      },
      liveExample: 'Live example',
      next: 'Next',
      changeNote:
        'Changing a pattern affects documents issued from now on. Numbers already issued keep the form they were minted with.',
    },

    templates: {
      /* Four groups, and only the first three ever print. The wording keeps that
         difference visible: the printed three are described as text on a
         document, the presets as a list the costing screen starts from. */
      included: 'Included',
      notIncluded: 'Not included',
      disclaimers: 'Disclaimers',
      costItems: 'Charge line presets',
      costItemsHint:
        'The charge lines a costing starts from. They are never printed on an offer and carry no amounts — the money is entered per quote.',
      new: 'New template',
      edit: 'Edit template',
      label: 'Label',
      body: 'Text',
      bodyHint: 'Appears verbatim on the offer. Write it as the client will read it.',
      bodyHintCostItem:
        'Optional. A remark for whoever prices the quote — it stays inside the company.',
      /* The reason a template can be deleted outright while every directory
         record is only deactivated: migration 0033 copies the body onto the
         quote, so removing one changes no document that ever quoted it. */
      removeHint: 'Removing a template does not change any offer already sent.',
    },

    /* Story 2.9 AC 4. Each hint names who reads the number, including the two
       that nobody reads yet — a settings field that silently changes nothing is
       how a founder learns the controls are decorative. */
    thresholds: {
      title: 'Reminder and attention thresholds',
      configured: 'These values were set for this company.',
      usingDefaults: 'No values saved yet, so the defaults below are in force.',
      followUp: 'Follow-up reminder',
      followUpHint: 'Days after an offer is sent with no answer',
      validityWarning: 'Validity expiry warning',
      validityWarningHint: 'Days before a quote’s validity date',
      stuckShipment: 'Stuck shipment',
      stuckShipmentHint: 'Days a case may go without movement',
      defaultIs: 'default {{days}}',
      range: 'Between {{min}} and {{max}} days',
      wholeDays: 'Enter a whole number of days.',
      outOfRange: 'Outside the range this setting accepts.',
      awaitingReminderJob: 'no reminder job reads this yet (Story 2.8)',
      readByDashboardEnv:
        'the dashboard currently reads ASA_DASHBOARD_STUCK_DAYS instead',
    },

    /* The direction catalogue (issue #35 story 1). A named route as an ordered
       list of points, each with a country and a normative transit time.
       Wording note: this screen never says "template", because on a case leg
       the same records are simply the routes on offer — and the one thing a
       Super-admin must understand before editing one is that applying it COPIES
       it, which is what the subtitle says rather than implying with a word. */
    directions: {
      title: 'Directions',
      subtitle:
        'Named routes a case leg is filled from. Applying one copies its points onto the leg, so editing a direction never moves a shipment already under way.',
      empty: 'No directions yet.',
      new: 'New direction',
      edit: 'Edit direction',
      inactive: 'off',
      activate: 'Switch on',
      deactivate: 'Switch off',
      pointCount_one: '{{count}} point',
      pointCount_other: '{{count}} points',
      normDays_one: '{{count}} day normative',
      normDays_other: '{{count}} days normative',
      name: 'Name',
      nameHint: 'How the desk says it — “Khorgos → Ashgabat (via Sarahs)”.',
      points: 'Points',
      pointsHint: 'In travel order. The first point is where the cargo starts.',
      pointPlaceholder: 'Khorgos',
      pointName: 'Point {{n}}',
      pointCountry: 'Country of point {{n}}',
      pointDays: 'Days to point {{n}}',
      daysPlaceholder: 'days',
      firstPointStart: 'start',
      addPoint: 'Add point',
      removePoint: 'Remove point {{n}}',
      maxPoints: 'A direction holds at most {{max}} points.',
      daysNote:
        'The day count sets the transition from the previous point. When it elapses, the cargo’s position on the case moves forward on its own.',
      delete: 'Delete this direction',
      deleteConfirm:
        'Delete it? Cases already filled from it keep their own copy and are unaffected.',
      deleteConfirmAction: 'Delete',
      errors: {
        minPoints: 'A direction needs at least two points.',
        wholeDays: 'Enter a whole number of days.',
      },
    },
  },

  auth: {
    /* The sign-in surface (FR-57, epics Story 1.2). Email and password, no 2FA
     * and no SSO in v1, so this copy IS the whole authentication story a user
     * ever reads.
     *
     * The failure copy is deliberately GENERIC and singular. Story 1.2 requires
     * the response body and timing to be identical whether or not the email
     * exists, so the endpoint cannot be used to discover who has an account.
     * Naming the wrong half — "no account with that email" — would hand back
     * from the client exactly what the server took care not to say. There is
     * therefore no per-field "unknown email" string here, and there must not be. */
    signIn: {
      title: 'Sign in',
      subtitle: 'Quotes, cases and money for the ASA team.',
      email: 'Email',
      emailPlaceholder: 'you@asagateway.com',
      password: 'Password',
      submit: 'Sign in',
      pending: 'Signing in',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      /* States what happened, then what to do. Never apologises, and never
         says which of the two fields was wrong. */
      failed: 'That email and password do not match',
      failedDetail: 'Check both and try again.',
      /* Five failures in fifteen minutes locks the email for fifteen minutes.
         The wait is named, because an unexplained refusal reads as an outage
         and turns into a message to a founder. */
      throttled: 'Too many attempts',
      throttledDetail: 'Sign-in for this email is paused for 15 minutes.',
      /* An idle or absolute session expiry lands here rather than at a dead
         end, so the reason for the interruption is stated once. */
      expired: 'Your session ended',
      expiredDetail: 'Sign in again to pick up where you left off.',
      /* Passwords are reset by a Super-admin (FR-57) — there is no self-serve
         reset in v1, so this says who to ask rather than offering a link that
         would go nowhere. */
      forgotten: 'Forgotten your password? A Super-admin can reset it for you.',
    },
    validation: {
      emailRequired: 'Enter your email',
      emailInvalid: 'Enter a valid email address',
      passwordRequired: 'Enter your password',
    },
    signOut: 'Sign out',
  },

  profile: {
    title: 'Profile',
    subtitle:
      'Your account, your role, and the language this interface speaks to you in.',
    loading: 'Loading profile',

    photo: {
      change: 'Change your photo',
      remove: 'Remove',
      /* Says the two things that get an upload refused, before it is refused. */
      hint: 'JPEG, PNG, WebP or GIF, up to 5 MB.',
      uploading: 'Sending your photo…',
      failed: 'The photo was not saved. Try again.',
      removeFailed: 'The photo was not removed. Try again.',
      rejected: {
        /* Names the reason rather than saying "that did not work" — otherwise
           people retry the same file and get the same silence. */
        type: 'That file is not a photo we can use. Choose a JPEG, PNG, WebP or GIF.',
        size: 'That photo is over 5 MB. Choose a smaller one.',
      },
    },

    language: {
      title: 'Language',
      description:
        'The interface language for your account. Documents sent to clients are always English; email bodies stay editable in any language.',
      error: 'The language was not changed. Try again.',
    },

    access: {
      title: 'Access',
      /* Read-only by design: the person reading this page is the one person who
       * may not change it, so it says who to ask. */
      description:
        'What your account may reach. Roles and directory ownership are set by a Super-admin on your user record in <0>Directories</0>.',
      noGrants:
        "You hold no directory ownership grants. Your role's own access is unaffected by this.",
      grants: {
        manageClients: 'Maintains the client directory',
        manageParticipants: 'Maintains participants and agents',
        manageTransport: 'Maintains transport reference data',
      },
    },
  },

  notFound: {
    title: 'Not found',
    /* Never say "the record does not exist" — a 404 is returned for both
       missing and not-owned, so existence is not leaked either way. */
    heading: 'This page is not available',
    description:
      'The link may be out of date, or the record may belong to another team.',
  },

  errors: {
    forbidden: {
      title: 'This section is not available to your role',
      detail: 'Ask a Super-admin if you need access.',
    },
    notFound: {
      title: 'This record is not available',
      detail: 'It may have been removed, or it belongs to another team.',
    },
    server: {
      title: 'The server could not answer',
      detail: 'The data was not changed. Try again in a moment.',
    },
    network: {
      title: 'Could not load this data',
      detail: 'Check the connection and try again.',
    },
    /* The interface itself failed, not a request. Unlike `server` above, this
       must NOT say "the data was not changed" — a crashed render knows nothing
       about what reached the server, and a reassurance we cannot back is worse
       than none. Say what to do instead. */
    crash: {
      title: 'Something went wrong',
      heading: 'This screen could not be displayed',
      detail:
        'Try again, or reload the page. If it keeps happening, send this to your administrator with what you were doing.',
      reload: 'Reload the page',
      details: 'Technical details',
    },
    /** Fallback detail for a problem document that carries no `detail`. */
    validation: {
      detail: 'Check the values and try again.',
    },
    /* The FR-64 refusal, in two shapes, for the three sheets that can be handed
       it: the offer, a case's invoice or debit note, and a client invoice. One
       form rather than three, because the sentence's subject is the ISSUING
       ENTITY and the screen that fixes it — never the paper that was refused.
       Naming the document would need three sentences to say the one thing the
       reader has to do, and the reader is already looking at the sheet that
       failed.
       The server names the fields its entity has not filled in, so the usual
       sentence is the one that lists them: "bank details" and "legal footer"
       are two different afternoons in Settings, and one sentence covering
       both used to name the footer over an entity whose footer was fine.
       The unnamed sentence is what remains when the refusal carried no field
       — an older server, or a field key this catalogue has no words for. It
       still points at Settings, because that is where the fix is either way,
       and it names nothing, because naming a field the server did not report
       is the bug the first sentence exists to fix. */
    branding: {
      incomplete: 'The issuing entity is missing its document details — no PDF can be produced without them. A super-admin can fill them in under Settings → Entities.',
      incompleteFields: 'The issuing entity is missing: {{fields}}. No PDF can be produced without them — a super-admin can fill them in under Settings → Entities.',
      /* The field names arrive as machine keys (`legal-footer`) and the words
         are ours (AD-27). Bare nouns rather than possessive phrases, because
         they are joined into a list. */
      field: {
        name: 'trading name',
        logo: 'logo',
        legalFooter: 'legal footer',
        bankDetails: 'bank details',
      },
    },
    /* One entry per suffix in the server's checked-in registry
       (platform/httpx/problem.go). The suffix is the contract; the words are
       ours, because a problem document's own title and detail are written for
       a developer reading a log (AD-22, AD-27). */
    problems: {
      validationFailed: {
        title: 'Some values need attention',
        detail: 'The highlighted fields could not be accepted.',
      },
      authRequired: {
        title: 'Your session has ended',
        detail: 'Sign in again to continue. Nothing you typed has been sent.',
      },
      forbidden: {
        title: 'This is not available to your role',
        detail: 'Ask a Super-admin if you need access.',
      },
      notFound: {
        title: 'This record is not available',
        detail: 'It may have been removed, or it belongs to another team.',
      },
      conflict: {
        title: 'This change conflicts with the record',
        detail: 'Reload to see the current state, then try again.',
      },
      idempotencyConflict: {
        title: 'This was already submitted',
        detail: 'The earlier submission stands. Reload to see it.',
      },
      rateLimited: {
        title: 'Too many attempts',
        detail: 'Wait a moment and try again.',
      },
      payloadTooLarge: {
        title: 'That is too large to send',
        detail: 'Reduce the size and try again.',
      },
      unsupportedMediaType: {
        title: 'That file type is not accepted',
        detail: 'Use one of the accepted formats.',
      },
      unavailable: {
        /* Not a fault: the feature depends on a service this installation has
           not configured. Says so, rather than reporting a server error nobody
           can act on. */
        title: 'This is not available here',
        detail: 'The feature needs a service that is not set up. Ask an administrator.',
      },
      internal: {
        title: 'The server could not answer',
        detail: 'Nothing was changed. Try again in a moment.',
      },
      unknown: {
        title: 'Something went wrong',
        detail: 'Try again. If it keeps happening, tell a Super-admin.',
      },
    },

    /* The connection bar. Persistent, never a toast: a toast that vanishes
       during a lost connection is worse than nothing (UX-DR-18). */
    connection: {
      offline: 'No connection. Your work is kept on screen and nothing has been sent.',
      restored: 'Connection restored.',
      retry: 'Retry now',
    },
    requestFailed: 'Request failed',
  },
} as const
